// ─── PART 11: CHARTS — Lightweight Charts v5 + Technical Indicators ───────────
// Exports: ChartsTab
//
// Data sources:
//   Alpaca REST   — OHLCV bars (1m,5m,15m,1h,4h,1D,1W,1M) via the backend proxy
//   Alpaca WS     — real-time trade ticks
//   Yahoo Finance — fallback OHLCV when Alpaca is unavailable
//
// Chart panes — ONE lightweight-charts instance, three panes:
//   pane 0  price   — candles + volume histogram + SMA/EMA/VWAP/BB overlays
//   pane 1  RSI     — 14-period, 70/30 price lines
//   pane 2  MACD    — histogram + MACD line + signal line
//
// ⚠⚠ RSI AND MACD USED TO BE SEPARATE RECHARTS CHARTS stacked under the price
// chart. They are now real panes of the same chart, which is what v5's
// addSeries(def, opts, paneIndex) buys: one shared time scale, one crosshair, and
// pan/zoom that moves all three together. Previously they scrolled independently
// of the candles they were meant to explain.
//
// Signal layers (optional, independent, fail silent):
//   DP        — dark-pool prints binned by price, net accumulation vs distribution
//   UO        — unusual options premium grouped by strike, calls vs puts
//
// ⚠⚠ THE BARRIERS LAYER WAS REMOVED FROM THIS CHART, AND ONLY FROM THIS CHART.
// The backend is untouched and still live: the /api/altdata/autocallable/*
// endpoints, sec_autocallable_barriers, the 5-minute autocall_monitor beat, the
// autocall:distance:* keys and the convergence detector's autocall path all
// continue to run and to score. What came off is the DISPLAY.
// ⚠ It was withdrawn because the levels it drew could not be trusted per-ticker.
// Measured on live filings: NVDA's rows are multi-underlier worst-of notes whose
// values belong to OTHER underliers — one stored an initial_value of $576.14
// (META's starting value, with NVDA's own $209.66 sitting in the same disclosure
// string) and another a 70% coupon barrier explicitly "with respect to the Class
// A common stock of Alphabet Inc." Both were filed under NVDA. A chart line is a
// confident claim about a price, and these were confidently wrong: the 70% level
// would have drawn 30% below spot looking entirely plausible.
// ⚠ Restore only once the parser attributes basket notes to the right underlier
// AND the display filters on extraction_confidence, which it never did.
//
// Drawing tools: lightweight-charts-drawing (peer-requires v5), five tools wired
// into the TOOLS row. Persistent for the life of the chart instance, which means
// they clear on a symbol change — see the note in LWPriceChart.
//
// ⚠⚠ KNOWN, UNFIXED, AND DELIBERATELY UNTOUCHED THIS SESSION: the real-time
// WebSocket below authenticates directly from the browser with keys.alpacaKey and
// keys.alpacaSecret. Bars are proxied through the backend precisely so those
// credentials never reach the client; this socket bypasses that and ships the
// secret to every user's browser. Nothing added in this rebuild widens the
// exposure — the three new signal layers are all backend-proxied and keyless —
// but the socket still needs to move behind the proxy.

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Line, Area, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
  ComposedChart, Legend,
} from "recharts";
import {
  createChart, createSeriesMarkers,
  CandlestickSeries, HistogramSeries, LineSeries,
} from "lightweight-charts";
import { DrawingManager, getToolRegistry } from "lightweight-charts-drawing";
import { AlertCircle, RefreshCw, Maximize2, Minimize2, Trash2 } from "lucide-react";
import {
  C, s, font, fmt, pct, fmtK,
  DataStatus, AnalystButton,
  ALPACA_REST, backendFetch,
} from "./core";
import { TickerNewsPanel } from "./TickerNewsPanel";
import { chartTimes, isDailyBars, chartTimeLabel, readoutText } from "./chartTime";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const TIMEFRAMES = [
  { label: "1m",  alpaca: "1Min",  limit: 390,  msPerBar: 60000        },
  { label: "5m",  alpaca: "5Min",  limit: 288,  msPerBar: 300000       },
  { label: "15m", alpaca: "15Min", limit: 192,  msPerBar: 900000       },
  { label: "1h",  alpaca: "1Hour", limit: 168,  msPerBar: 3600000      },
  { label: "4h",  alpaca: "4Hour", limit: 120,  msPerBar: 14400000     },
  { label: "1D",  alpaca: "1Day",  limit: 252,  msPerBar: 86400000     },
  { label: "1W",  alpaca: "1Week", limit: 104,  msPerBar: 604800000    },
  { label: "1M",  alpaca: "1Month",limit: 60,   msPerBar: 2592000000   },
];

const OVERLAYS = ["SMA20", "SMA50", "SMA200", "EMA20", "VWAP", "BB"];
const INDICATORS = ["RSI", "MACD", "VOLUME"];

// ─── TECHNICAL INDICATOR MATH ────────────────────────────────────────────────
// Self-contained — no external library required for these core indicators.
// Matches technicalindicators npm output for drop-in compatibility.

function calcSMA(data, period) {
  const result = new Array(data.length).fill(null);
  for (let i = period - 1; i < data.length; i++) {
    const slice = data.slice(i - period + 1, i + 1);
    result[i] = slice.reduce((a, b) => a + b, 0) / period;
  }
  return result;
}

function calcEMA(data, period) {
  const result = new Array(data.length).fill(null);
  const k = 2 / (period + 1);
  let ema = data[0];
  result[0] = ema;
  for (let i = 1; i < data.length; i++) {
    ema = data[i] * k + ema * (1 - k);
    result[i] = i >= period - 1 ? ema : null;
  }
  return result;
}

function calcVWAP(bars) {
  let cumTPV = 0, cumVol = 0;
  return bars.map((b) => {
    const tp = (b.high + b.low + b.close) / 3;
    cumTPV += tp * b.volume;
    cumVol += b.volume;
    return cumVol > 0 ? cumTPV / cumVol : null;
  });
}

function calcBollingerBands(data, period = 20, stdDev = 2) {
  const sma    = calcSMA(data, period);
  const upper  = new Array(data.length).fill(null);
  const lower  = new Array(data.length).fill(null);
  for (let i = period - 1; i < data.length; i++) {
    const slice  = data.slice(i - period + 1, i + 1);
    const mean   = sma[i];
    const variance = slice.reduce((a, b) => a + (b - mean) ** 2, 0) / period;
    const sd     = Math.sqrt(variance);
    upper[i] = mean + stdDev * sd;
    lower[i] = mean - stdDev * sd;
  }
  return { sma, upper, lower };
}

function calcRSI(data, period = 14) {
  const result = new Array(data.length).fill(null);
  if (data.length < period + 1) return result;
  let gains = 0, losses = 0;
  for (let i = 1; i <= period; i++) {
    const diff = data[i] - data[i - 1];
    if (diff > 0) gains  += diff;
    else          losses -= diff;
  }
  let avgGain = gains / period;
  let avgLoss = losses / period;
  result[period] = 100 - 100 / (1 + avgGain / (avgLoss || 0.0001));
  for (let i = period + 1; i < data.length; i++) {
    const diff = data[i] - data[i - 1];
    avgGain  = (avgGain  * (period - 1) + Math.max(0, diff)) / period;
    avgLoss  = (avgLoss  * (period - 1) + Math.max(0, -diff)) / period;
    result[i] = 100 - 100 / (1 + avgGain / (avgLoss || 0.0001));
  }
  return result;
}

function calcMACD(data, fast = 12, slow = 26, signal = 9) {
  const emaFast   = calcEMA(data, fast);
  const emaSlow   = calcEMA(data, slow);
  const macdLine  = data.map((_, i) =>
    emaFast[i] != null && emaSlow[i] != null ? emaFast[i] - emaSlow[i] : null
  );
  const validMacd = macdLine.filter((v) => v != null);
  const sigLine   = calcEMA(validMacd, signal);
  const sigFull   = new Array(data.length).fill(null);
  let sigIdx = 0;
  macdLine.forEach((v, i) => { if (v != null) { sigFull[i] = sigLine[sigIdx++] ?? null; } });
  const histogram = data.map((_, i) =>
    macdLine[i] != null && sigFull[i] != null ? macdLine[i] - sigFull[i] : null
  );
  return { macdLine, signalLine: sigFull, histogram };
}

// ─── BAR FETCH — Alpaca primary, Yahoo fallback ───────────────────────────────
async function fetchBars(symbol, timeframe, keys) {
  const tf = TIMEFRAMES.find((t) => t.label === timeframe) || TIMEFRAMES[5]; // default 1D
  const end   = new Date().toISOString();
  const start = new Date(Date.now() - tf.limit * tf.msPerBar * 1.4).toISOString();

  // ── BACKEND HOOK ──
  // When backend live: fetch(`${window.QUANT_SIGNAL_BACKEND}/api/bars/${symbol}?tf=${timeframe}`)
  // Backend caches bars, serves all users from one upstream call

  // Alpaca primary (backend holds the credentials)
  if (window.QUANT_SIGNAL_BACKEND) {
    try {
      const res = await backendFetch(
        `${ALPACA_REST}/stocks/${symbol}/bars` +
        `?timeframe=${tf.alpaca}&start=${start}&end=${end}&limit=${tf.limit}&feed=iex`,
        `/api/bars/${symbol}?tf=${timeframe}`
      );
      const data = await res.json();
      if (data.bars?.length) {
        return {
          bars: data.bars.map((b) => ({
            time:   Math.floor(new Date(b.t).getTime() / 1000),
            open:   b.o, high: b.h, low: b.l, close: b.c,
            volume: b.v, vwap: b.vw,
            date:   new Date(b.t).toLocaleDateString("en", { month: "short", day: "numeric" }),
          })),
          source: "alpaca",
        };
      }
    } catch {}
  }

  // Yahoo Finance fallback
  try {
    const yahooInterval = {
      "1m": "1m", "5m": "5m", "15m": "15m", "1h": "60m",
      "4h": "60m", "1D": "1d", "1W": "1wk", "1M": "1mo",
    }[timeframe] || "1d";
    const range = { "1m": "1d", "5m": "5d", "15m": "1mo", "1h": "1mo",
      "4h": "3mo", "1D": "1y", "1W": "2y", "1M": "5y" }[timeframe] || "1y";
    const res = await fetch(
      `https://query1.finance.yahoo.com/v8/finance/chart/${symbol}` +
      `?interval=${yahooInterval}&range=${range}`
    );
    const data = await res.json();
    const result = data?.chart?.result?.[0];
    if (result?.timestamp?.length) {
      const { timestamp, indicators } = result;
      const q = indicators.quote[0];
      const bars = timestamp.map((t, i) => ({
        time:   t,
        open:   q.open[i],
        high:   q.high[i],
        low:    q.low[i],
        close:  q.close[i],
        volume: q.volume[i],
        date:   new Date(t * 1000).toLocaleDateString("en", { month: "short", day: "numeric" }),
      })).filter((b) => b.open && b.close);
      return { bars, source: "yahoo" };
    }
  } catch {}

  return { bars: [], source: null };
}

// ─── TICKER SEARCH ────────────────────────────────────────────────────────────
async function searchTickers(query, keys) {
  if (!query || query.length < 1) return [];
  try {
    if (window.QUANT_SIGNAL_BACKEND) {
      const res = await backendFetch(
        `https://data.alpaca.markets/v2/assets?asset_class=us_equity&status=active`,
        `/api/assets/search?q=${encodeURIComponent(query)}`
      );
      const data = await res.json();
      return (Array.isArray(data) ? data : [])
        .slice(0, 8)
        .map((a) => ({ symbol: a.symbol, name: a.name }));
    }
  } catch {}
  // Fallback — common symbols
  const common = [
    { symbol: "AAPL",  name: "Apple Inc" },
    { symbol: "MSFT",  name: "Microsoft Corporation" },
    { symbol: "NVDA",  name: "Nvidia Corporation" },
    { symbol: "GOOGL", name: "Alphabet Inc" },
    { symbol: "AMZN",  name: "Amazon.com Inc" },
    { symbol: "META",  name: "Meta Platforms Inc" },
    { symbol: "TSLA",  name: "Tesla Inc" },
    { symbol: "SPY",   name: "SPDR S&P 500 ETF" },
    { symbol: "QQQ",   name: "Invesco QQQ Trust" },
    { symbol: "GLD",   name: "SPDR Gold Shares" },
    { symbol: "TLT",   name: "iShares 20+ Year Treasury" },
    { symbol: "USO",   name: "United States Oil Fund" },
  ];
  return common.filter((c) =>
    c.symbol.startsWith(query.toUpperCase()) ||
    c.name.toUpperCase().includes(query.toUpperCase())
  );
}


// ─── CLICK-TO-CREATE ──────────────────────────────────────────────────────────
// ⚠⚠ THIS LAYER EXISTS BECAUSE THE PACKAGE DOES NOT HAVE ONE.
// lightweight-charts-drawing@0.1.1 renders, hit-tests, selects and drags
// drawings, but nothing in it creates one from user input. Proven against the
// shipped bundle: `_activeTool` occurs four times in 326 KB — the setter, the
// getter, the event payload, and one read in handleClick that only checks it is
// FALSY before doing selection. handleMouseDown/Move/Up drag existing selected
// drawings by their anchors and nothing else. Arming every one of the five tools
// and firing chart-click, press, move, release and a second click produced zero
// drawings each time.
// What DOES work is registry.createDrawing(...) followed by manager.addDrawing(),
// so the missing piece is only the click→anchor→construct step, which is this.
//
// ⚠ Anchor counts are READ FROM THE REGISTRY (`requiredAnchors`), never hardcoded:
// trend-line/rectangle/fib-retracement take 2, horizontal-line/text-annotation
// take 1, and a hardcoded table would silently desync if a tool is ever swapped.
const DRAW_STYLE = {
  lineColor: "#9D4EDD",
  lineWidth: 2,
  fillColor: "rgba(157, 78, 221, 0.12)",
};

function toolEntry(type) {
  try { return getToolRegistry().get(type) || null; } catch { return null; }
}

// ⚠ TextAnnotation defaults to backgroundColor "rgba(255,255,255,0.9)" — a white
// box on a #1E2330 chart. Overridden here rather than left to the package.
function buildDrawing(type, anchors, id) {
  const reg = (() => { try { return getToolRegistry(); } catch { return null; } })();
  if (!reg) return null;
  let options = {};
  if (type === "text-annotation") {
    // The only tool that needs a value the chart cannot infer from a click.
    // An empty or cancelled prompt aborts the drawing rather than dropping an
    // empty box on the chart.
    const txt = typeof window === "undefined" ? "" : window.prompt("Annotation text", "");
    if (!txt || !txt.trim()) return null;
    options = {
      text: txt.trim(),
      fontSize: 12,
      fontFamily: font,
      backgroundColor: "rgba(30, 35, 48, 0.92)",
      borderColor: "#9D4EDD",
    };
  }
  try {
    return reg.createDrawing(type, id, anchors, DRAW_STYLE, options);
  } catch { return null; }
}

// ⚠ Tool ids are the registry's own strings, verified against
// getToolRegistry().getAll() — the manager does NOT validate an unknown id, it
// silently accepts it and then never enters drawing mode. "trendline" looks
// right and does nothing; "trend-line" is the real one.
const DRAW_TOOLS = [
  { id: "trend-line",      label: "TREND" },
  { id: "horizontal-line", label: "HORIZ" },
  { id: "rectangle",       label: "RECT"  },
  { id: "fib-retracement", label: "FIB"   },
  { id: "text-annotation", label: "TEXT"  },
];

// id -> the toolbar's own label, so the on-chart hint and the button agree.
const DRAW_LABEL = DRAW_TOOLS.reduce((m, t) => { m[t.id] = t.label; return m; }, {});

const SIGNAL_LAYERS = [
  { id: "dp",       label: "DP"       },
  { id: "uo",       label: "UO"       },
];

// ⚠⚠ BINARY SEARCH ON `time`, NOT A LINEAR FIND ON `date`.
// The bug this replaces: `bars.find(b => b.date === sig.date)` compared a DISPLAY
// STRING produced by toLocaleDateString ("Aug 27"). At any intraday resolution
// every bar in a session carries the identical string, so the first bar of the
// day matched every signal and the whole layer collapsed onto one candle. It was
// invisible because exactly one squeeze marker fed the layer; it becomes wrong
// the moment a second source does — which this rebuild adds three of.
// Returns the bar at or immediately before the signal's timestamp, or null when
// the signal predates the loaded window.
function alignToBar(bars, sig) {
  if (!bars?.length) return null;
  const t = sigTime(sig);
  if (t == null) return bars[bars.length - 1];   // undated signal pins to the last bar
  if (t <= bars[0].time) return bars[0];
  if (t >= bars[bars.length - 1].time) return bars[bars.length - 1];
  let lo = 0, hi = bars.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (bars[mid].time <= t) lo = mid; else hi = mid - 1;
  }
  return bars[lo];
}

// Accepts unix seconds, unix millis, or anything Date can parse. Returns unix
// SECONDS to match the bar key, or null when there is nothing usable.
function sigTime(sig) {
  const raw = sig?.time ?? sig?.timestamp ?? sig?.ts ?? null;
  if (raw == null) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return raw > 1e11 ? Math.floor(raw / 1000) : Math.floor(raw);
  }
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

// ─── SIGNAL LAYER FETCHES ─────────────────────────────────────────────────────
// ⚠ Every one of these resolves to null on ANY failure — network, non-2xx, bad
// shape. The caller treats null as "this layer has no data for this ticker" and
// leaves the toggle off. There is deliberately no error surface: a side layer
// that 404s must not put an error banner over a price chart that loaded fine.
async function fetchDarkPoolHistory(symbol) {
  try {
    const res = await backendFetch(null, `/api/darkpool/history/${symbol}?days=7`);
    if (!res?.ok) return null;
    const data = await res.json();
    const prints = data?.prints || data?.rows || (Array.isArray(data) ? data : null);
    return Array.isArray(prints) && prints.length ? prints : null;
  } catch { return null; }
}

// ⚠ Two endpoints, in order. The per-ticker history route is the one that belongs
// here; /recent is market-wide and has to be filtered client-side, so it is only
// the fallback for a backend that does not carry the history route.
async function fetchUnusualForTicker(symbol) {
  try {
    const res = await backendFetch(null, `/api/stream/unusual/history/${symbol}?days=7`);
    if (res?.ok) {
      const data = await res.json();
      const rows = data?.signals || data?.rows || (Array.isArray(data) ? data : null);
      if (Array.isArray(rows) && rows.length) return rows;
    }
  } catch { /* fall through to the market-wide feed */ }
  try {
    const res = await backendFetch(null, `/api/stream/unusual/recent`);
    if (!res?.ok) return null;
    const data = await res.json();
    const rows = data?.signals || data?.rows || (Array.isArray(data) ? data : []);
    const mine = rows.filter((r) =>
      String(r.underlying || r.ticker || r.symbol || "").toUpperCase() === symbol);
    return mine.length ? mine : null;
  } catch { return null; }
}



// ─── PRICE CHART FALLBACK ─────────────────────────────────────────────────────
// ⚠ This used to be a full hand-written Recharts candlestick — a second, parallel
// implementation of the price chart that existed because lightweight-charts was
// not yet an installed dependency. It is now, so the duplicate is gone and this
// is an honest error state. Two renderers for one chart meant every fix had to be
// made twice, and the fallback silently drifted from the real one.
function PriceChartFallback({ reason }) {
  return (
    <div style={{
      height: 320, display: "flex", alignItems: "center", justifyContent: "center",
      flexDirection: "column", gap: 6, color: C.textDim, fontSize: 13,
    }}>
      <AlertCircle size={14} />
      <span>Chart failed to initialise{reason ? ` — ${reason}` : ""}.</span>
      <span style={{ fontSize: 11 }}>Reload the tab, or try another symbol.</span>
    </div>
  );
}

// ─── SIGNAL LAYER MATH ────────────────────────────────────────────────────────
// Two optional layers over the price chart. They are deliberately not
// lightweight-charts series: neither is a function of TIME. DP and UO are
// distributions over PRICE, and modelling them as series would force a fake time
// key on every datum. Both now render as markers on the candlestick series.
//
// ⚠ ALL THREE FAIL SILENTLY AND INDEPENDENTLY. A failed fetch yields null, the
// toggle stays off, and the chart renders exactly as it would have. There is no
// error state for missing signal data — a chart that refuses to draw because a
// side layer 404'd is worse than a chart without the layer.

// Index and mega-cap ETFs price-bin coarsely: a $0.50 bin on SPY at $650 makes
// 1,300 bins across a session and every one of them holds a single print.
const INDEX_TICKERS = new Set([
  "SPY", "QQQ", "IWM", "DIA", "VOO", "VTI", "IVV", "SPX", "NDX", "RUT",
  "TLT", "HYG", "LQD", "GLD", "SLV", "USO", "XLF", "XLE", "XLK", "SMH",
]);

// ⚠ Bin size is a function of PRICE LEVEL, not of the ticker's identity, except
// for indices. A $0.10 bin is meaningful on a $7 stock and absurd on a $900 one.
function dpBinSize(price, symbol) {
  if (INDEX_TICKERS.has(String(symbol || "").toUpperCase())) return 1.0;
  if (!(price > 0)) return 0.25;
  if (price < 10) return 0.10;
  if (price <= 100) return 0.25;
  return 0.50;
}

// ⚠ Accumulation minus distribution, NOT gross notional. A price level with
// $50M bought and $50M sold is not a level where anything happened; summing the
// absolute values would draw the tallest bar in the chart there.
function binDarkPoolPrints(prints, symbol, spot) {
  if (!Array.isArray(prints) || !prints.length) return null;
  const size = dpBinSize(spot, symbol);
  const bins = new Map();
  for (const p of prints) {
    const price = Number(p.price);
    const notional = Number(p.notional) || (Number(p.size) || 0) * (price || 0);
    if (!(price > 0) || !(notional > 0)) continue;
    const key = Math.round(price / size) * size;
    const cur = bins.get(key) || { price: key, buy: 0, sell: 0, n: 0 };
    // The classifier's own read wins; is_buyer / is_seller are the fallback for
    // rows that predate it.
    const cls = String(p.classification || "").toUpperCase();
    const buy = cls.includes("ACCUM") || cls.includes("BUY") || p.is_buyer === true;
    const sell = cls.includes("DISTRIB") || cls.includes("SELL") || p.is_seller === true;
    if (buy) cur.buy += notional;
    else if (sell) cur.sell += notional;
    else { cur.buy += notional / 2; cur.sell += notional / 2; }  // unclassified: neutral
    cur.n += 1;
    bins.set(key, cur);
  }
  const out = [...bins.values()]
    .map((b) => ({ ...b, net: b.buy - b.sell }))
    .filter((b) => Math.abs(b.net) > 0);
  if (!out.length) return null;
  const peak = Math.max(...out.map((b) => Math.abs(b.net)));
  return { bins: out, peak, binSize: size };
}

// ⚠ Grouped by STRIKE, which is already discrete — no binning needed and none
// applied. Call premium and put premium are summed separately so the net keeps
// its sign; a strike with equal call and put flow is not a directional strike.
function groupUnusualByStrike(signals) {
  if (!Array.isArray(signals) || !signals.length) return null;
  const bins = new Map();
  for (const s of signals) {
    const strike = Number(s.strike);
    // ⚠⚠ BOTH NAMES, AND NEITHER IS OPTIONAL. The two sources that feed this
    // layer disagree on one field and agree on every other:
    //   /api/stream/unusual/history/{ticker}  ->  premium        (raw archive column)
    //   /api/stream/unusual/recent            ->  total_premium  (the stream shape)
    // Reading only `premium` silently zeroed every row from the fallback and the
    // > 0 guard below discarded them all — no error, no rows, and the [UO] pill
    // simply never appeared. Reading only `total_premium` would do the same to
    // the history endpoint, which is the PRIMARY source. Accept either.
    // ⚠ ?? not ||, so a genuine 0 premium is not silently rewritten by the other
    // field; the plausibility guard on the next line is what rejects it.
    const prem = Number(s.total_premium ?? s.premium) || 0;
    if (!(strike > 0) || !(prem > 0)) continue;
    const type = String(s.option_type || s.type || "").toUpperCase();
    const cur = bins.get(strike) || { price: strike, call: 0, put: 0, n: 0 };
    if (type.startsWith("C")) cur.call += prem;
    else if (type.startsWith("P")) cur.put += prem;
    else continue;
    cur.n += 1;
    bins.set(strike, cur);
  }
  const out = [...bins.values()]
    .map((b) => ({ ...b, net: b.call - b.put }))
    .filter((b) => Math.abs(b.net) > 0);
  if (!out.length) return null;
  return { bins: out, peak: Math.max(...out.map((b) => Math.abs(b.net))) };
}


// ─── DP / UO CHART MARKERS ────────────────────────────────────────────────────
// ⚠⚠ THESE REPLACE THE BOTTOM-BAND SVG HISTOGRAM, AND THE REASON IS MEASUREMENT.
// The band drew price-binned bars at y = h - 2 - barHeight, where h is the FULL
// container. With the RSI and MACD panes on — which is the default — the bottom
// of the container is the MACD pane and the time axis, so the bars painted over
// another indicator instead of under the candles. Worse, bar height scaled off
// the maximum: measured on 245 real NVDA prints, the median bin was 0.3% of peak,
// so 28 of 36 bars clamped to a 1px line at 0.55 opacity. They were rendering,
// and they were invisible.
//
// Markers on the candlestick series fix both at once: the library places them on
// the bar itself, inside the price pane, at whatever the pane geometry happens to
// be, and a marker has a fixed size rather than one scaled off an outlier.
//
// ⚠ SHAPE IS SQUARE vs CIRCLE, NOT SQUARE vs TRIANGLE. lightweight-charts v5
// supports exactly four marker shapes — "circle" | "square" | "arrowUp" |
// "arrowDown". There is no triangle and no diamond. Square (DP) against circle
// (UO) is the available way to meet the requirement the shapes existed for:
// telling the two apart at a glance on the same candle.
// ⚠⚠ ONE COLOUR PER KIND, AND DIRECTION IS NOT IN THE COLOUR ANY MORE.
// These sit ON the candles, and the candles are already green and red. A green
// accumulation marker on a green candle and a red distribution marker on a red
// candle both vanished into the bar they were drawn on — the marker was legible
// only when it happened to disagree with the candle, which is precisely the case
// where it mattered least. Yellow and magenta appear nowhere in the candle
// palette or the chart chrome, so both read at a glance against an up bar, a
// down bar and the panel background alike.
// ⚠ SHAPE now carries the kind (square = dark pool, circle = unusual options)
// and the TOOLTIP carries the direction. Nothing is lost — the direction was
// never readable from a colour competing with the candle underneath it.
// ⚠ Alpha stays at 0.62. A marker drawn on the bar must not hide the bar; that
// is the whole reason these are "inBar" rather than floating above it.
const DP_MARK = "rgba(255, 215, 0, 0.62)";     // #FFD700 gold
const UO_MARK = "rgba(255, 0, 255, 0.62)";     // #FF00FF magenta
const MARKER_SIZE = 1;

// ⚠⚠ DROPS OUT-OF-RANGE ROWS. alignToBar CLAMPS to the first or last bar, which is
// right for a signal that belongs to the visible window but catastrophic here: the
// DP feed is seven days deep while a 1m chart holds one session, so clamping would
// pile several hundred prints onto the first candle — the same collapse the
// display-string bug caused, arriving by a different route. A print outside the
// loaded window has no bar and gets no marker.
function alignToBarStrict(bars, t) {
  if (!bars?.length || t == null) return null;
  if (t < bars[0].time) return null;
  const last = bars[bars.length - 1];
  // ⚠ No upper bound beyond the last bar's own span: the final bar is still
  // forming, so a print inside it is legitimately "on" it.
  if (t > last.time + (bars.length > 1 ? last.time - bars[bars.length - 2].time : 0)) {
    return null;
  }
  let lo = 0, hi = bars.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (bars[mid].time <= t) lo = mid; else hi = mid - 1;
  }
  return bars[lo];
}

// ⚠ ONE MARKER PER BAR PER TYPE, aggregating everything that landed on it. Two
// markers at the same position on the same bar overdraw into a single smudge, and
// 245 prints on a 252-bar daily chart would be exactly that. The aggregate also
// makes a far better tooltip than any single print does.
function buildDpMarkers(prints, bars) {
  if (!Array.isArray(prints) || !prints.length || !bars?.length) return [];
  const byBar = new Map();
  for (const p of prints) {
    const t = sigTime(p);
    const bar = alignToBarStrict(bars, t);
    if (!bar) continue;
    const price = Number(p.price);
    const notional = Number(p.notional) || (Number(p.size) || 0) * (price || 0);
    if (!(notional > 0)) continue;
    const cls = String(p.classification || "").toUpperCase();
    const buy = cls.includes("ACCUM") || cls.includes("BUY") || p.is_buyer === true;
    const sell = cls.includes("DISTRIB") || cls.includes("SELL") || p.is_seller === true;
    const cur = byBar.get(bar.time)
      || { time: bar.time, buy: 0, sell: 0, n: 0, top: null, topN: 0 };
    if (buy) cur.buy += notional;
    else if (sell) cur.sell += notional;
    else { cur.buy += notional / 2; cur.sell += notional / 2; }
    cur.n += 1;
    if (notional > cur.topN) { cur.topN = notional; cur.top = p; }
    byBar.set(bar.time, cur);
  }
  return [...byBar.values()].map((b) => {
    const net = b.buy - b.sell;
    return {
      time: b.time,
      position: "inBar",
      shape: "square",
      color: DP_MARK,
      size: MARKER_SIZE,
      id: `dp:${b.time}`,
      _kind: "dp",
      _n: b.n,
      _net: net,
      _gross: b.buy + b.sell,
      _tier: String(b.top?.classification || "").toUpperCase() || "UNCLASSIFIED",
      _strength: b.top?.signal_strength ?? null,
    };
  });
}

function buildUoMarkers(signals, bars) {
  if (!Array.isArray(signals) || !signals.length || !bars?.length) return [];
  const byBar = new Map();
  for (const s of signals) {
    const t = sigTime(s);
    const bar = alignToBarStrict(bars, t);
    if (!bar) continue;
    // ⚠ Both field names — the archive route returns `premium`, the live stream
    // route returns `total_premium`. Reading one silently zeroed every row from
    // the other and the layer rendered nothing at all.
    const prem = Number(s.total_premium ?? s.premium) || 0;
    if (!(prem > 0)) continue;
    const type = String(s.option_type || s.type || "").toUpperCase();
    const cur = byBar.get(bar.time)
      || { time: bar.time, call: 0, put: 0, n: 0, top: null, topP: 0 };
    if (type.startsWith("C")) cur.call += prem;
    else if (type.startsWith("P")) cur.put += prem;
    else continue;
    cur.n += 1;
    if (prem > cur.topP) { cur.topP = prem; cur.top = s; }
    byBar.set(bar.time, cur);
  }
  return [...byBar.values()].map((b) => {
    const net = b.call - b.put;
    return {
      time: b.time,
      position: "inBar",
      shape: "circle",
      color: UO_MARK,
      size: MARKER_SIZE,
      id: `uo:${b.time}`,
      _kind: "uo",
      _n: b.n,
      _net: net,
      _gross: b.call + b.put,
      _signal: b.top?.signal || null,
      _strike: b.top?.strike ?? null,
      // A finite number or null: a NaN/string here would print "NaN% OTM".
      _otm: typeof b.top?.otm_pct === "number" && Number.isFinite(b.top.otm_pct) ? b.top.otm_pct : null,
      _type: String(b.top?.option_type || "").toUpperCase(),
    };
  });
}

// ET clock for the tooltips. The bar time is unix seconds.
// Tooltip content, per marker kind. Returns [{t, c}] — first line is the header.
function markerTipLines(m) {
  const when = markerClock(m.time);
  if (m._kind === "dp") {
    const acc = m._net >= 0;
    return [
      { t: `DARK POOL · ${acc ? "ACCUMULATION" : "DISTRIBUTION"}`,
        c: acc ? "#22c55e" : "#FF5252" },
      { t: `net ${acc ? "+" : "-"}${markerMoney(m._net)} of ${markerMoney(m._gross)}` },
      { t: `${m._n} print${m._n === 1 ? "" : "s"} · tier ${m._tier}` },
      { t: `${when} ET` },
    ];
  }
  const call = m._net >= 0;
  const lines = [
    { t: `UNUSUAL OPTIONS · ${call ? "CALL" : "PUT"}-DOMINATED`,
      c: call ? "#2DD4BF" : "#f97316" },
    { t: `net ${call ? "+" : "-"}${markerMoney(m._net)} of ${markerMoney(m._gross)}` },
    { t: `${m._n} signal${m._n === 1 ? "" : "s"}`
        + (m._signal ? ` · ${String(m._signal).replace(/_/g, " ")}` : "") },
  ];
  if (m._strike != null) {
    lines.push({ t: `top strike ${m._strike}${m._type ? ` ${m._type[0]}` : ""}`
      + (m._otm != null ? ` · ${m._otm}% OTM` : "") });
  }
  lines.push({ t: `${when} ET` });
  return lines;
}

function markerClock(t) {
  try {
    return new Date(t * 1000).toLocaleString("en-US", {
      timeZone: "America/New_York", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
  } catch { return ""; }
}

function markerMoney(v) {
  const n = Math.abs(Number(v) || 0);
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}


// ─── LIGHTWEIGHT CHARTS v5 PRICE CHART ────────────────────────────────────────
// ⚠⚠ MIGRATED v4 → v5. Every series is now created through the unified
// chart.addSeries(SeriesDefinition, options, paneIndex) call, and markers are a
// separate primitive rather than a series method. The v4 forms — addCandlestickSeries,
// addLineSeries, addHistogramSeries, series.setMarkers — no longer exist.
// The third argument to addSeries is what makes RSI and MACD real panes inside
// this chart instead of the separate Recharts charts they used to be: they now
// share the price chart's time scale, crosshair and pan/zoom for free.
// ─── BOLLINGER BAND FILL ──────────────────────────────────────────────────────
// ⚠⚠ A SERIES PRIMITIVE, NOT A SERIES. lightweight-charts has no series type that
// fills between two arbitrary lines: AreaSeries fills to a baseline coordinate and
// BaselineSeries fills to a baseline PRICE, and neither can track a second moving
// line. The two obvious bodges are worse than they look — stacking an area series
// painted in the panel colour would occlude the grid and any series beneath it,
// and drawing into an SVG overlay above the canvas would put the band ON TOP of
// the candles rather than behind them. Both of those were tried and removed;
// this is why the band is a primitive.
// A primitive with zOrder "bottom" is the one mechanism
// v5 offers that renders beneath its own series, which is exactly what "behind the
// candles" means.
// ⚠ Rebuilt with the chart, never mutated: the band data is captured in a closure
// and the whole chart is torn down whenever bars or overlays change, so there is no
// stale-data path to guard.
const BB_FILL = "rgba(157, 78, 221, 0.10)";   // C.purple #9D4EDD at 10%

function createBBFillPrimitive(bands) {
  let chartApi = null;
  let seriesApi = null;

  const renderer = {
    draw(target) {
      if (!chartApi || !seriesApi || !bands?.length) return;
      const ts = chartApi.timeScale();
      target.useBitmapCoordinateSpace((scope) => {
        const ctx = scope.context;
        const hr = scope.horizontalPixelRatio;
        const vr = scope.verticalPixelRatio;

        // ⚠ Drawn as CONTIGUOUS RUNS, not one polygon. Bollinger bands are null for
        // the first `period - 1` bars, and a single path across that gap would
        // close the shape through the leading edge of the chart and flood the area
        // left of the first valid band.
        let run = [];
        const flush = () => {
          if (run.length >= 2) {
            ctx.beginPath();
            ctx.moveTo(run[0].x, run[0].yu);
            for (let i = 1; i < run.length; i++) ctx.lineTo(run[i].x, run[i].yu);
            for (let i = run.length - 1; i >= 0; i--) ctx.lineTo(run[i].x, run[i].yl);
            ctx.closePath();
            ctx.fill();
          }
          run = [];
        };

        ctx.save();
        ctx.fillStyle = BB_FILL;
        for (const b of bands) {
          const x = ts.timeToCoordinate(b.time);
          const yu = seriesApi.priceToCoordinate(b.upper);
          const yl = seriesApi.priceToCoordinate(b.lower);
          if (x === null || yu === null || yl === null) { flush(); continue; }
          run.push({ x: x * hr, yu: yu * vr, yl: yl * vr });
        }
        flush();
        ctx.restore();
      });
    },
  };

  const paneView = {
    renderer: () => renderer,
    // Beneath the candles, above the grid.
    zOrder: () => "bottom",
  };

  return {
    paneViews: () => [paneView],
    updateAllViews: () => {},
    attached: (param) => { chartApi = param.chart; seriesApi = param.series; },
    detached: () => { chartApi = null; seriesApi = null; },
  };
}

const OVERLAY_STYLE = {
  sma20:   { color: "#f59e0b", title: "SMA 20",  dashed: false },
  sma50:   { color: "#3b82f6", title: "SMA 50",  dashed: false },
  sma200:  { color: "#a855f7", title: "SMA 200", dashed: false },
  ema20:   { color: "#00ff87", title: "EMA 20",  dashed: false },
  vwap:    { color: "#ec4899", title: "VWAP",    dashed: true  },
  bbUpper: { color: "#9D4EDD", title: "BB Upper", dashed: true },
  bbMid:   { color: "#9D4EDD", title: "BB Mid",   dashed: true },
  bbLower: { color: "#9D4EDD", title: "BB Lower", dashed: true },
};

// Pane order is fixed so a toggle never reshuffles what is already on screen.
const PANE_PRICE = 0;

function LWPriceChart({
  bars, overlayData, signals, showVolume, showRSI, showMACD, rsiData, macdData,
  drawTool, drawNonce, clearNonce, onDrawComplete, height = 320,
  dp, uo, layerShow, dpRows, uoRows,
}) {
  const containerRef = useRef(null);
  const [failed, setFailed] = useState(null);
  const [api, setApi] = useState(null);         // {chart, series} for the overlay
  const drawRef = useRef(null);
  // ⚠ The armed tool is read through a REF inside the click handler, not closed
  // over. The handler is registered once when the chart is built; closing over
  // the prop would freeze it at whatever was armed at build time, and putting
  // drawTool in the chart effect's deps would rebuild the chart on every tool
  // click and erase every drawing the user had made.
  const drawToolRef = useRef(null);
  const pendingRef  = useRef([]);   // anchors captured so far for the armed tool
  const drawSeqRef  = useRef(0);    // per-chart id counter; addDrawing rejects dupes
  const [pending, setPending] = useState(0);   // drives the "1 / 2" hint only
  const [markerTip, setMarkerTip] = useState(null);
  // id -> marker, rebuilt with the chart so a stale id can never resolve.
  const markerMapRef = useRef(new globalThis.Map());
  // ⚠ The crosshair readout is written straight to this node, not through state:
  // it changes on every pointer move, and a re-render per move would re-render
  // the whole overlay tree for one line of text.
  const readoutRef = useRef(null);

  useEffect(() => {
    if (failed) return;
    const el = containerRef.current;
    if (!el || !bars.length) return;

    let chart;
    try {
      // ⚠⚠ EASTERN AT THE BOUNDARY. `ct[i]` is bar i's time shifted to Eastern
      // wall clock (see chartTime.js); every series and marker below takes ct,
      // never b.time. null = a bar the shift could not place in order (DST
      // fall-back on a 24h symbol) and it is left out of every series.
      const ct = chartTimes(bars);
      const ctByTime = new globalThis.Map();
      bars.forEach((b, i) => { if (ct[i] != null) ctByTime.set(b.time, ct[i]); });
      const hasTime = (d) => d && d.time != null;
      const daily = isDailyBars(bars);

      chart = createChart(el, {
        width: el.clientWidth || 600,
        height,
        layout: {
          background: { color: C.panel },
          textColor: C.textDim,
          fontFamily: font,
          fontSize: 14,
          // v5 renders pane separators; make them match the panel chrome.
          panes: { separatorColor: C.border, separatorHoverColor: C.borderHi,
            enableResize: true },
        },
        grid: {
          vertLines: { color: C.border },
          horzLines: { color: C.border },
        },
        crosshair: { mode: 1 },
        rightPriceScale: { borderColor: C.border },
        // Daily and longer bars carry no meaningful clock time; intraday shows it.
        timeScale: { borderColor: C.border, timeVisible: !daily, secondsVisible: false },
        // The crosshair's time label: "Oct 22, 2026 09:47 ET", or the date alone.
        localization: { timeFormatter: (time) => chartTimeLabel(time, daily) },
      });

      const candleSeries = chart.addSeries(CandlestickSeries, {
        upColor: C.green, downColor: "#FF5252",
        borderUpColor: C.green, borderDownColor: "#FF5252",
        wickUpColor: C.green, wickDownColor: "#FF5252",
      }, PANE_PRICE);
      candleSeries.setData(bars.map((b, i) => ({
        time: ct[i], open: b.open, high: b.high, low: b.low, close: b.close,
      })).filter(hasTime));

      if (showVolume) {
        const volSeries = chart.addSeries(HistogramSeries, {
          priceFormat: { type: "volume" },
          priceScaleId: "",
        }, PANE_PRICE);
        volSeries.priceScale().applyOptions({ scaleMargins: { top: 0.82, bottom: 0 } });
        volSeries.setData(bars.map((b, i) => ({
          time: ct[i],
          value: b.volume || 0,
          color: b.close >= b.open ? C.green + "88" : "#FF525288",
        })).filter(hasTime));
      }

      Object.keys(OVERLAY_STYLE).forEach((key) => {
        const seriesVals = overlayData[key];
        if (!seriesVals) return;
        const style = OVERLAY_STYLE[key];
        const line = chart.addSeries(LineSeries, {
          color: style.color,
          lineWidth: 1,
          lineStyle: style.dashed ? 2 : 0,
          priceLineVisible: false,
          lastValueVisible: false,
        }, PANE_PRICE);
        line.setData(
          bars.map((b, i) => ({ time: ct[i], value: seriesVals[i] }))
            .filter((d) => d.value != null && hasTime(d))
        );
      });

      // ⚠ Attached AFTER the overlay lines so the two BB lines keep their own
      // styling; the fill is additive and neither replaces nor restyles them.
      // Both edges must be present — a fill against a missing lower band is not a
      // band, and half of one is more misleading than none.
      if (overlayData.bbUpper && overlayData.bbLower) {
        const bands = bars
          .map((b, i) => ({
            time: ct[i],
            upper: overlayData.bbUpper[i],
            lower: overlayData.bbLower[i],
          }))
          .filter((d) => d.upper != null && d.lower != null && hasTime(d));
        if (bands.length >= 2) {
          candleSeries.attachPrimitive(createBBFillPrimitive(bands));
        }
      }

      // ── RSI pane ──
      let paneIdx = PANE_PRICE;
      if (showRSI && rsiData?.length) {
        paneIdx += 1;
        const rsi = chart.addSeries(LineSeries, {
          color: C.purple, lineWidth: 1, priceLineVisible: false,
          lastValueVisible: true,
          priceFormat: { type: "price", precision: 1, minMove: 0.1 },
        }, paneIdx);
        rsi.setData(bars.map((b, i) => ({ time: ct[i], value: rsiData[i] }))
          .filter((d) => d.value != null && hasTime(d)));
        // 70/30 reference lines — createPriceLine is the v5 equivalent of the
        // Recharts ReferenceLine the old pane drew.
        rsi.createPriceLine({ price: 70, color: "#FF5252", lineWidth: 1, lineStyle: 2,
          axisLabelVisible: true, title: "70" });
        rsi.createPriceLine({ price: 30, color: C.green, lineWidth: 1, lineStyle: 2,
          axisLabelVisible: true, title: "30" });
        try { chart.panes()[paneIdx]?.setStretchFactor(0.35); } catch { /* older build */ }
      }

      // ── MACD pane ──
      if (showMACD && macdData?.macdLine?.length) {
        paneIdx += 1;
        const hist = chart.addSeries(HistogramSeries, {
          priceFormat: { type: "price", precision: 3, minMove: 0.001 },
          priceLineVisible: false,
        }, paneIdx);
        hist.setData(bars.map((b, i) => {
          const v = macdData.histogram?.[i];
          return v == null || ct[i] == null ? null : {
            time: ct[i], value: v, color: v >= 0 ? C.green + "aa" : "#FF5252aa",
          };
        }).filter(Boolean));
        const macdLine = chart.addSeries(LineSeries, {
          color: C.blue, lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
        }, paneIdx);
        macdLine.setData(bars.map((b, i) => ({ time: ct[i], value: macdData.macdLine?.[i] }))
          .filter((d) => d.value != null && hasTime(d)));
        const sigLine = chart.addSeries(LineSeries, {
          color: "#f59e0b", lineWidth: 1, priceLineVisible: false, lastValueVisible: false,
        }, paneIdx);
        sigLine.setData(bars.map((b, i) => ({ time: ct[i], value: macdData.signalLine?.[i] }))
          .filter((d) => d.value != null && hasTime(d)));
        try { chart.panes()[paneIdx]?.setStretchFactor(0.35); } catch { /* older build */ }
      }

      // ── Signal markers ──
      // ⚠⚠ MATCHED ON `time`, NOT ON `date`. `date` is a DISPLAY STRING built by
      // toLocaleDateString ("Aug 27"), so at every intraday resolution EVERY bar in
      // a session carries the identical value and `find` returned the first bar of
      // the day for every signal — silently stacking them all on one candle. `time`
      // is unix seconds and unique per bar. The bars array is already ascending, so
      // a binary search is exact and O(log n); the previous linear find over a
      // display string was neither.
      const sigMarkers = (signals || [])
        .map((sig) => {
          const bar = alignToBar(bars, sig);
          if (!bar) return null;
          return {
            time: bar.time,
            position: "aboveBar",
            color: sig.color || C.amber,
            shape: "arrowDown",
            text: sig.label,
          };
        })
        .filter(Boolean);

      // ⚠⚠ DP AND UO AS MARKERS ON THE CANDLE ITSELF. position "inBar" is the
      // library placing them inside the bar's own body, so they follow the price
      // pane wherever the pane layout puts it — which is exactly what the old
      // bottom-of-container band could not do once RSI and MACD were enabled.
      // ⚠ Colours carry alpha so the candle stays readable underneath.
      const dpMarkers = layerShow?.dp ? buildDpMarkers(dpRows, bars) : [];
      const uoMarkers = layerShow?.uo ? buildUoMarkers(uoRows, bars) : [];

      const mmap = new globalThis.Map();
      for (const m of [...dpMarkers, ...uoMarkers]) mmap.set(m.id, m);
      markerMapRef.current = mmap;

      // ⚠ ASCENDING BY TIME IS A LIBRARY REQUIREMENT, not a preference —
      // createSeriesMarkers renders an unsorted array incorrectly rather than
      // throwing. Three independent sources are merged here, so the sort has to
      // happen after the merge, never per source.
      // ⚠ Markers are BUILT on real bar times (alignment and the ET tooltip read
      // them, and markerMapRef keeps those originals); only the copies handed to
      // the library are moved onto the Eastern chart time.
      const allMarkers = [...sigMarkers, ...dpMarkers, ...uoMarkers]
        .map((m) => ({ ...m, time: ctByTime.get(m.time) }))
        .filter(hasTime)
        .sort((a, b) => a.time - b.time);
      if (allMarkers.length) {
        createSeriesMarkers(candleSeries, allMarkers, { zOrder: "aboveSeries" });
      }

      // ⚠ Hover comes from the crosshair, because a canvas marker has no DOM node
      // to attach a listener to. hoveredInfo.objectKind tells us a marker was hit
      // and objectId is the id we stamped on it.
      // ── Crosshair readout ──
      // Date and time (Eastern) plus O/H/L/C of the bar under the crosshair;
      // the latest bar when there is no crosshair (pointer away, or a phone
      // between long-presses).
      const lastIdx = (() => { for (let i = bars.length - 1; i >= 0; i--) if (ct[i] != null) return i; return -1; })();
      const restingText = lastIdx >= 0 ? readoutText(ct[lastIdx], bars[lastIdx], daily) : "";
      const setReadout = (txt) => { if (readoutRef.current) readoutRef.current.textContent = txt; };
      setReadout(restingText);

      const onCross = (param) => {
        const ohlc = param?.time != null ? param.seriesData?.get(candleSeries) : null;
        setReadout(ohlc ? readoutText(param.time, ohlc, daily) : restingText);
        const info = param?.hoveredInfo;
        const id = info?.objectId ?? param?.hoveredObjectId;
        const m = id != null ? markerMapRef.current.get(String(id)) : null;
        if (!m || !param?.point) { setMarkerTip(null); return; }
        setMarkerTip({
          x: param.point.x, y: param.point.y,
          lines: markerTipLines(m),
        });
      };
      chart.subscribeCrosshairMove(onCross);

      chart.timeScale().fitContent();
      setApi({ chart, series: candleSeries });

      // ── Drawing tools ──
      // ⚠ The manager owns its own pointer handling on the container; it is
      // attached once per chart instance and detached with it. Drawings live for
      // the life of that instance, which is exactly the "persistent within the
      // session, cleared on symbol change" behaviour asked for — a symbol change
      // rebuilds the chart, which disposes the manager and its drawings with it.
      let manager = null;
      try {
        manager = new DrawingManager();
        manager.attach(chart, candleSeries, el);
        drawRef.current = manager;
      } catch (e) {
        drawRef.current = null;   // drawing is optional; the chart still works
      }

      // ⚠ Our own click subscription, independent of the manager's. The manager
      // subscribes too, but its handler returns immediately whenever a tool is
      // armed, so the two never contend: with a tool armed only this one acts,
      // and with none armed only the manager's selection logic acts.
      const onChartClick = (param) => {
        const tool = drawToolRef.current;
        const mgr = drawRef.current;
        if (!tool || !mgr || !param?.point) return;
        const entry = toolEntry(tool);
        if (!entry) return;
        // param.time is the bar under the cursor and snaps cleanly; it is absent
        // when the click lands past the last bar, where the raw coordinate is the
        // only answer available.
        const time = param.time ?? chart.timeScale().coordinateToTime(param.point.x);
        const price = candleSeries.coordinateToPrice(param.point.y);
        if (time == null || price == null) return;

        pendingRef.current.push({ time, price });
        const need = entry.requiredAnchors || 1;
        if (pendingRef.current.length < need) {
          setPending(pendingRef.current.length);
          return;
        }
        const anchors = pendingRef.current.slice(0, need);
        pendingRef.current = [];
        setPending(0);
        drawSeqRef.current += 1;
        const drawing = buildDrawing(tool, anchors, `d${drawSeqRef.current}`);
        if (drawing) {
          try { mgr.addDrawing(drawing); } catch { /* rejected: id clash */ }
        }
        // Disarm once a drawing lands, so the next click pans or selects rather
        // than starting another shape the user did not ask for. Re-arming the same
        // tool is one click, and drawNonce makes that work.
        onDrawComplete?.();
      };
      chart.subscribeClick(onChartClick);

      const ro = new ResizeObserver((entries) => {
        const wpx = entries[0]?.contentRect?.width;
        if (wpx) chart.applyOptions({ width: Math.floor(wpx) });
      });
      ro.observe(el);

      return () => {
        ro.disconnect();
        try { chart.unsubscribeClick(onChartClick); } catch { /* chart gone */ }
        try { chart.unsubscribeCrosshairMove(onCross); } catch { /* chart gone */ }
        markerMapRef.current = new globalThis.Map();
        setMarkerTip(null);
        if (readoutRef.current) readoutRef.current.textContent = "";
        pendingRef.current = [];
        try { manager?.detach(); } catch { /* already gone */ }
        drawRef.current = null;
        setApi(null);
        chart.remove();
      };
    } catch (e) {
      if (chart) { try { chart.remove(); } catch { /* nothing to clean */ } }
      setFailed(e?.message ? String(e.message).slice(0, 80) : null);
    }
    // ⚠ dpRows / uoRows / layerShow are dependencies BECAUSE the markers are built
    // inside this effect. Toggling a pill has to rebuild them, and the chart is
    // rebuilt with it — acceptable here because a toggle is a deliberate user act,
    // unlike the drawing-tool arm which must never rebuild (it would erase every
    // drawing) and is therefore kept in its own effect reading a ref.
  }, [bars, overlayData, signals, showVolume, showRSI, showMACD, rsiData, macdData,
    height, failed, dpRows, uoRows, layerShow]);

  // Active tool changes must NOT rebuild the chart — that would wipe every
  // drawing the user has made. Separate effect, separate dependency.
  useEffect(() => {
    drawToolRef.current = drawTool || null;
    // Switching or disarming a tool abandons any half-placed shape rather than
    // carrying its first anchor into the next tool.
    pendingRef.current = [];
    setPending(0);
    const m = drawRef.current;
    if (!m) return;
    // ⚠ Still set on the manager even though it does not create anything: an
    // armed tool is what suppresses its click-to-select, which would otherwise
    // fire on the same clicks we are collecting as anchors.
    try { m.setActiveTool(drawTool || null); } catch { /* tool id unknown */ }
  }, [drawTool, drawNonce, api]);

  // Escape abandons a partly-placed drawing. The fullscreen handler in the
  // parent also listens for Escape; these are independent and both are wanted.
  useEffect(() => {
    if (!pending) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      pendingRef.current = [];
      setPending(0);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pending]);

  useEffect(() => {
    if (!clearNonce) return;
    pendingRef.current = [];
    setPending(0);
    const m = drawRef.current;
    if (!m) return;
    try { m.clearAll(); } catch { /* nothing to clear */ }
  }, [clearNonce]);

  if (failed) return <PriceChartFallback reason={failed} />;

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <div ref={containerRef} style={{ width: "100%", height }} />
      {/* Crosshair readout, top-left of the price pane. Hidden while a drawing
          tool is armed (its hint takes this corner); below the marker tooltip
          (zIndex 6), which may cover it for as long as a marker is hovered.
          The right margin keeps it off the price scale. */}
      <div ref={readoutRef} data-readout="1" style={{
        position: "absolute", top: 6, left: 8, right: 64, zIndex: 3, pointerEvents: "none",
        display: drawTool ? "none" : "block",
        fontSize: 11, lineHeight: 1.4, color: C.textMid, fontFamily: font,
        textShadow: `0 0 3px ${C.panel}, 0 0 3px ${C.panel}`,
      }} />
      {/* A two-anchor tool gives no feedback between the first and second
          click without this — the chart looks inert and the click looks lost. */}
      {drawTool && (
        <div style={{
          position: "absolute", top: 6, left: 8, zIndex: 4, pointerEvents: "none",
          background: "rgba(30,35,48,0.9)", border: `1px solid ${C.purple}`,
          borderRadius: 3, padding: "2px 7px", fontSize: 11, color: C.text,
        }}>
          {(() => {
            const need = toolEntry(drawTool)?.requiredAnchors || 1;
            return need > 1
              ? `${DRAW_LABEL[drawTool] || drawTool}: click ${pending + 1} of ${need}`
              : `${DRAW_LABEL[drawTool] || drawTool}: click to place`;
          })()}
          <span style={{ color: C.textDim }}> · Esc cancels</span>
        </div>
      )}
      {/* ⚠ Marker tooltip. The MARKERS are canvas, drawn by the library; only
          this readout is DOM, because a hover panel cannot be anything else. */}
      {markerTip && (
        <div style={{
          position: "absolute", left: Math.max(6, Math.min((markerTip.x || 0) + 12, 9999)),
          top: Math.max(6, (markerTip.y || 0) - 8), zIndex: 6, pointerEvents: "none",
          background: C.panel, border: `1px solid ${C.borderHi}`, borderRadius: 3,
          padding: "6px 9px", fontSize: 11, color: C.text, minWidth: 170,
          boxShadow: "0 6px 18px rgba(0,0,0,0.6)",
        }}>
          {markerTip.lines.map((l, i) => (
            <div key={i} style={{ color: i === 0 ? l.c : C.textMid,
              fontWeight: i === 0 ? 700 : 400, marginTop: i ? 2 : 0 }}>
              {l.t}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── OPTIONS PAYOFF DIAGRAM ────────────────────────────────────────────────────
function PayoffDiagram({ spot, symbol }) {
  // ⚠ The initial position is EMPTY when no price is available yet. It used to be
  // seeded unconditionally at `strike: spot || 100`, and because a useState
  // initialiser runs EXACTLY ONCE, a panel opened before currentPrice resolved was
  // left holding a 100-strike leg for the rest of the session — on any ticker, at
  // any price. Starting empty hands the job to the seeding effect below, which
  // fills in a true ATM strike the moment a price lands. Showing "+ ADD LEG" for a
  // beat is better than showing a strike that belongs to nothing.
  // Lazy initialiser: the object is built on mount only, not rebuilt every render.
  const [legs, setLegs] = useState(() =>
    spot > 0 ? [{ type: "call", direction: "long", strike: spot, premium: 3.50, qty: 1 }] : []
  );
  // ⚠ `atExpiry` state removed — it was declared and never read. The panel is
  // at-expiration only, and that is stated in the header ("P&L AT EXPIRATION")
  // and the footer note, not driven by state. If a before-expiry curve is ever
  // added it needs a real toggle plus a time-value model, not this flag back.

  // ── Reset the position when the chart's ticker changes ──
  // A payoff built from NVDA strikes is meaningless on SPY: priceRange rebuilds
  // its window around the NEW spot (0.7×–1.3×) while the legs keep the OLD
  // ticker's strikes, so every sampled P&L is constant and the breakeven scan at
  // :727 reports "—" or a spurious crossing. Silent reset to one ATM leg.
  //
  // ⚠⚠ TWO effects, not one, and the reason is a race worth stating. loadChart
  // sets currentPrice ASYNCHRONOUSLY and never clears it, so at the instant
  // `symbol` changes the `spot` prop still holds the OUTGOING ticker's price.
  // Seeding on the symbol change alone would strike the new position at the old
  // ticker's price — the same wrong-instrument bug in a quieter form, and harder
  // to spot because the number looks plausible. So: clear on the change, capture
  // the outgoing price, and seed only once a DIFFERENT price arrives.
  const legsSymbolRef   = useRef(symbol);
  const outgoingSpotRef = useRef(null);

  useEffect(() => {
    if (legsSymbolRef.current === symbol) return;   // same ticker — leave edits alone
    legsSymbolRef.current = symbol;
    outgoingSpotRef.current = spot ?? null;         // previous ticker's price
    setLegs([]);                                    // seeded by the effect below
  }, [symbol]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // ⚠ Only ever seeds an EMPTY position, and the user cannot empty one by hand:
    // the ✕ button renders only while legs.length > 1. So an empty legs array only
    // ever comes from one of two places we control — the ticker-change clear above,
    // or a mount that had no price yet — never from a user action, which is what
    // makes seeding here safe without tracking whether the position was edited.
    if (legs.length) return;
    if (!(spot > 0)) return;                        // no price yet
    if (spot === outgoingSpotRef.current) return;   // still the previous ticker's
    outgoingSpotRef.current = null;
    setLegs([{ type: "call", direction: "long", strike: spot, premium: 3.50, qty: 1 }]);
  }, [legs.length, spot]);

  // ⚠ No invented fallback. `spot || 100` inserted a strike belonging to no
  // instrument, and once inserted nothing could correct it — the seeding effect
  // only ever fills an EMPTY position, so a leg added during the pre-price window
  // kept its 100 strike for the session. Order of preference: the live spot, then
  // the strike of the last leg already on screen (a spread builds around what the
  // user placed), and refuse when there is neither rather than fabricate one.
  // ⚠ `> 0` also rejects NaN, which the strike input yields on an emptied field.
  const nextLegStrike = spot > 0 ? spot : legs[legs.length - 1]?.strike;
  const canAddLeg = nextLegStrike > 0;

  const addLeg = () => {
    if (!canAddLeg) return;   // belt-and-braces: the button is disabled in this state
    setLegs((p) => [...p, { type: "call", direction: "long", strike: nextLegStrike, premium: 1.00, qty: 1 }]);
  };
  const removeLeg = (i) => setLegs((p) => p.filter((_, idx) => idx !== i));
  const updateLeg = (i, key, val) => setLegs((p) => p.map((l, idx) => idx === i ? { ...l, [key]: val } : l));

  // Compute payoff across price range
  const priceRange = useMemo(() => {
    if (!spot) return [];
    const min = spot * 0.7;
    const max = spot * 1.3;
    const steps = 60;
    return Array.from({ length: steps }, (_, i) => {
      const price = min + (i / (steps - 1)) * (max - min);
      let pnl = 0;
      legs.forEach((leg) => {
        const { type, direction, strike, premium, qty } = leg;
        let legPnl = 0;
        if (type === "call") {
          legPnl = Math.max(0, price - strike) - premium;
        } else {
          legPnl = Math.max(0, strike - price) - premium;
        }
        pnl += (direction === "long" ? legPnl : -legPnl) * qty * 100;
      });
      return { price: fmt(price), pnl: Math.round(pnl) };
    });
  }, [legs, spot]);

  // Key metrics
  const breakevens = [];
  for (let i = 1; i < priceRange.length; i++) {
    if ((priceRange[i-1].pnl < 0) !== (priceRange[i].pnl < 0)) {
      breakevens.push(((parseFloat(priceRange[i-1].price) + parseFloat(priceRange[i].price)) / 2).toFixed(2));
    }
  }
  // ⚠ priceRange is [] until `spot` resolves, and Math.max() of no arguments is
  // -Infinity — which rendered a literal "$-Infinity" as MAX PROFIT on every
  // open-before-price-lands. null here, "—" at the render sites below.
  const hasRange  = priceRange.length > 0;
  const maxProfit = hasRange ? Math.max(...priceRange.map((d) => d.pnl)) : null;
  const maxLoss   = hasRange ? Math.min(...priceRange.map((d) => d.pnl)) : null;

  return (
    <div style={s.panel}>
      <div style={s.panelH}>
        <span style={s.panelT}>OPTIONS PAYOFF DIAGRAM</span>
        <span style={{ ...s.tag(), fontSize: 11 }}>P&L AT EXPIRATION · PER POSITION</span>
      </div>
      <div style={s.panelB}>
        {/* Leg builder */}
        <div style={{ marginBottom: 10 }}>
          {legs.map((leg, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center", flexWrap: "wrap" }}>
              <select style={{ ...s.inp, width: "auto" }} value={leg.direction}
                onChange={(e) => updateLeg(i, "direction", e.target.value)}>
                <option value="long">LONG</option>
                <option value="short">SHORT</option>
              </select>
              <select style={{ ...s.inp, width: "auto" }} value={leg.type}
                onChange={(e) => updateLeg(i, "type", e.target.value)}>
                <option value="call">CALL</option>
                <option value="put">PUT</option>
              </select>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 13, color: C.textDim }}>K</span>
                <input style={{ ...s.inp, width: 80 }} type="number" value={leg.strike}
                  onChange={(e) => updateLeg(i, "strike", parseFloat(e.target.value))} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 13, color: C.textDim }}>Prem</span>
                <input style={{ ...s.inp, width: 70 }} type="number" step="0.01" value={leg.premium}
                  onChange={(e) => updateLeg(i, "premium", parseFloat(e.target.value))} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 13, color: C.textDim }}>Qty</span>
                <input style={{ ...s.inp, width: 50 }} type="number" min="1" value={leg.qty}
                  onChange={(e) => updateLeg(i, "qty", parseInt(e.target.value))} />
              </div>
              {legs.length > 1 && (
                <button style={{ ...s.btn(), padding: "3px 7px", color: C.red }} onClick={() => removeLeg(i)}>✕</button>
              )}
            </div>
          ))}
          {/* ⚠ The opacity override is not decoration: s.btn() sets an explicit
              `color`, so the browser's default disabled greying is overridden and
              a bare `disabled` would leave the button looking live while doing
              nothing. `disabled` itself matches the codebase convention. */}
          <button
            style={{ ...s.btn(), padding: "3px 10px", fontSize: 11,
                     opacity: canAddLeg ? 1 : 0.45, cursor: canAddLeg ? "pointer" : "default" }}
            onClick={addLeg}
            disabled={!canAddLeg}
            title={canAddLeg ? undefined : "Waiting for a price"}
          >+ ADD LEG</button>
        </div>

        {/* Key metrics */}
        <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
          <div>
            <div style={s.lbl}>MAX PROFIT</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: maxProfit > 1e6 ? C.teal : C.green }}>
              {maxProfit === null ? "—" : maxProfit > 999999 ? "UNLIMITED" : `$${maxProfit.toLocaleString()}`}
            </div>
          </div>
          <div>
            <div style={s.lbl}>MAX LOSS</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: maxLoss < -999999 ? "#FF5252" : "#FF5252" }}>
              {maxLoss === null ? "—" : maxLoss < -999999 ? "UNLIMITED" : `$${Math.abs(maxLoss).toLocaleString()}`}
            </div>
          </div>
          <div>
            <div style={s.lbl}>BREAKEVEN{breakevens.length > 1 ? "S" : ""}</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.amber }}>
              {breakevens.length ? breakevens.map((b) => `$${b}`).join(" / ") : "—"}
            </div>
          </div>
          {spot && (
            <div>
              <div style={s.lbl}>CURRENT SPOT</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>${fmt(spot)}</div>
            </div>
          )}
        </div>

        {/* Payoff chart */}
        {priceRange.length > 0 && (
          <ResponsiveContainer width="100%" height={160}>
            <ComposedChart data={priceRange} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
              <XAxis dataKey="price" tick={{ fontSize: 10, fill: C.textDim }} tickLine={false}
                interval={Math.floor(priceRange.length / 6)} tickFormatter={(v) => `$${v}`} />
              <YAxis tick={{ fontSize: 10, fill: C.textDim }} tickLine={false} axisLine={false}
                tickFormatter={(v) => `$${v.toLocaleString()}`} width={60} orientation="right" />
              {/* ⚠ labelStyle/itemStyle are required, not cosmetic: contentStyle
                  sets only the background, so the label and any series without an
                  explicit colour fall through to recharts' #000 default — black on
                  the dark panel. Same defect the MacroTab USREC tooltip carried.
                  Matches the VolSmile / Divergence tooltip precedents. */}
              <Tooltip
                contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11, fontFamily: font }}
                labelStyle={{ color: C.text }}
                itemStyle={{ color: C.text }}
                formatter={(v) => [`$${v.toLocaleString()}`, "P&L"]}
                labelFormatter={(l) => `Price: $${l}`}
              />
              <ReferenceLine y={0} stroke={C.border} strokeWidth={1} />
              {spot && <ReferenceLine x={fmt(spot)} stroke={C.amber} strokeDasharray="4 2"
                label={{ value: "SPOT", fill: C.amber, fontSize: 10 }} />}
              <Area
                type="monotone"
                dataKey="pnl"
                stroke={C.teal}
                strokeWidth={2}
                fill="transparent"
                dot={false}
              />
              {/* ⚠ A second series bound to dataKey="pnl" used to sit here — a <Bar>
                  whose shape returned null. It drew nothing, but a shape-nulled
                  series still registers a tooltip payload entry, and with no `fill`
                  recharts coloured it #000: the hover card showed the P&L twice,
                  the second copy in unreadable black. Removed rather than
                  recoloured — it was a duplicate, not a datum. */}
            </ComposedChart>
          </ResponsiveContainer>
        )}
        <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>
          P&L shown per position (qty × 100 shares per contract) at expiration.
          Does not account for early exercise, dividends, or time value before expiry.{" "}
          <span style={{ color: "#FFD700", fontWeight: 700 }}>Not a recommendation.</span>
        </div>
      </div>
    </div>
  );
}

// ─── VOL SMILE CHAIN ─────────────────────────────────────────────────────────
// ⚠⚠ Charts loads its OWN options chain rather than reading whatever OptionsTab
// last pushed to the bus. Two separate reasons:
//   1. `globalSignals.optionsChain` has a single writer that only runs once a
//      user has visited the OPTIONS tab, so the smile was permanently empty on
//      any session that never went there — there was no auto-load at all.
//   2. That payload used to carry no ticker, so the smile could render one
//      symbol's chain against THIS chart's spot. Mismatched pairs either drew an
//      empty chart (every strike stripped by the ±30% filter) or, when the two
//      prices happened to sit close, a plausible-looking but wrong curve.
// The bus is still used, but only as a same-symbol fast start — see the effect.
//
// Same two endpoints OptionsTab uses; never a new API.

// IV is normalised to a PERCENT so both sources land on one axis: the backend
// ships decimals (greeks.mid_iv 0.1279 / impliedVolatility) while the bus payload
// is already a ×100 string, and VolSmile's parseFloat reads either.
// ⚠ The `n > 0` test is the expired-chain guard. AlpacaService._as_float coerces
// a missing greek to 0.0, so an expired expiry serves a full, shape-valid chain
// with every IV zeroed; those rows are dropped here instead of plotting a 0% smile.
function normaliseSmileChain(data) {
  // ⚠ Rounded, not just multiplied: 0.1279 * 100 is 12.790000000000001 in binary
  // float. The chart formatters hide it, but it would diverge from the bus payload
  // (which OptionsTab stores via .toFixed) and leak into any future consumer.
  const pct = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) && n > 0 ? Math.round(n * 10000) / 100 : null;
  };
  // alpaca / tradier: one flat `options` array tagged with option_type
  const flat = Array.isArray(data?.options) ? data.options : (data?.options?.option || []);
  if (flat.length) {
    const mk = (o) => ({ strike: o.strike, iv: pct(o.greeks?.mid_iv) });
    return {
      calls: flat.filter((o) => o.option_type === "call").map(mk).filter((o) => o.iv),
      puts:  flat.filter((o) => o.option_type === "put").map(mk).filter((o) => o.iv),
    };
  }
  // yahoo_approx: pre-split { calls: [...], puts: [...] }
  const mkY = (o) => ({ strike: o.strike, iv: pct(o.impliedVolatility) });
  return {
    calls: (data?.calls || []).map(mkY).filter((o) => o.iv),
    puts:  (data?.puts  || []).map(mkY).filter((o) => o.iv),
  };
}

// How many expiries deep to look for one that actually carries greeks.
const SMILE_MAX_EXPIRY_TRIES = 3;

// ⚠⚠ expirations[0] IS A LIVE EXPIRY AND STILL HAS NO GREEKS FOR MOST OF THE DAY.
// The backend drops today's expiry only after the 16:00 ET close, so from 09:30
// to 16:00 the nearest entry is the 0DTE contract — and Alpaca serves that chain
// with every greek 0.0 while it is actively trading. Measured 2026-08-07 10:23 ET:
// SPY 08-07 → 336 rows, 331 with real volume, 0 with mid_iv > 0, gex_valid false;
// the very next expiry (08-10) → 169 rows with IV, gex_valid true. Same shape on
// QQQ / AAPL / NVDA / MPT. normaliseSmileChain's n > 0 guard then dropped every
// row and the panel showed its empty state for the whole session — every
// 0DTE-listed name, every trading day, not just expiry Fridays.
// ⚠ So the smile walks forward to the first expiry that yields plottable points,
// up to SMILE_MAX_EXPIRY_TRIES. Nothing usable in three → return the last attempt
// so the caller still shows the honest empty state.
//
// ⚠⚠ THE TEST IS "DID WE GET POINTS", NOT `gex_valid === false` ALONE, and the
// difference is load-bearing: the backend hardcodes gex_valid false on its YAHOO
// fallback branch because Yahoo ships no greeks — but that payload carries
// `impliedVolatility`, which normaliseSmileChain reads and which plots fine.
// Skipping purely on the flag would walk past three perfectly good Yahoo chains
// and blank a symbol that renders today. gex_valid is what diagnosed this; the
// point count is what the smile actually needs.
// ⚠ Cost is bounded and only paid in the broken case: a chain that yields points
// returns on the first request exactly as before. A symbol with no IV anywhere
// costs three chain fetches per toggle, each cached 300s server-side.
async function fetchSmileChain(symbol) {
  const expRes = await backendFetch(
    `https://query1.finance.yahoo.com/v7/finance/options/${symbol}`,
    `/api/options/expirations/${symbol}`
  );
  const expData = await expRes.json();
  const list = Array.isArray(expData?.expirations)
    ? expData.expirations
    : (expData?.expirations?.date || []);
  const candidates = list
    .slice(0, SMILE_MAX_EXPIRY_TRIES)
    .map((d) => String(d).slice(0, 10))
    .filter(Boolean);
  if (!candidates.length) return { calls: [], puts: [], expiration: null };

  let last = { calls: [], puts: [], expiration: candidates[0] };
  for (const expiration of candidates) {
    const expTs = Math.floor(new Date(expiration).getTime() / 1000);
    const res = await backendFetch(
      `https://query1.finance.yahoo.com/v7/finance/options/${symbol}?date=${expTs}`,
      `/api/options/chain/${symbol}/${expiration}`,
      { headers: { Accept: "application/json" } }
    );
    const data = await res.json();
    const chain = { ...normaliseSmileChain(data), expiration };
    // ⚠ The expiry is reported from the chain that was actually plotted, so the
    // panel header and the Heston expiry gate both follow the walk rather than
    // still naming expirations[0].
    if (chain.calls.length || chain.puts.length) return chain;
    last = chain;
  }
  return last;
}

// ─── VOLATILITY SMILE ────────────────────────────────────────────────────────
function VolSmile({ calls, puts, spot, hestonSurface, expiration }) {
  // ⚠⚠ THIS HOOK MUST STAY ABOVE EVERY EARLY RETURN BELOW. React requires an
  // identical hook order on every render, and this component has three exits
  // (no chain, no spot, nothing plottable). The guards therefore live INSIDE the
  // memo as well, and the early returns follow it.
  // ⚠⚠ MEMOISED FOR IDENTITY, NOT FOR CPU. App holds a `clock` state ticking
  // every 1000ms (App.jsx), ChartsTab is not memoised, so this component
  // re-rendered once a second — and an unmemoised build handed <ComposedChart> a
  // brand-new `data` array each time. Recharts compares `data !== prevData` in
  // getDerivedStateFromProps: a new identity bumps updateId, rebuilds the axis
  // maps and recomputes the tooltip payload from the PREVIOUS render's ticks,
  // mid-hover, once per second. Rebuilding 123 rows is cheap; the stable
  // reference is the point.
  const withHeston = useMemo(() => {
    // ⚠ A non-positive spot makes every moneyness Infinity/NaN — no rows rather
    // than rows measured against a meaningless denominator.
    if (!(spot > 0)) return [];
    // ⚠⚠ ONE ROW PER STRIKE, with the put and the call as SEPARATE SERIES. Both
    // sides used to share a single `iv` key, so a strike listing both produced two
    // rows at the same x. Measured on a live SPY chain: 169 rows over 123 distinct
    // moneyness values — 46 x-positions carrying two materially different readings
    // (e.g. -0.91%: put 10.81% vs call 7.25%). On the category axis they became two
    // adjacent slots with the SAME label, so a pixel of mouse movement flipped the
    // tooltip between them and the single line drew a vertical zig-zag between the
    // pair. Keyed on `strike` rather than the derived moneyness: moneyness is a pure
    // function of strike and spot, so the strike is the identity without float-key
    // equality risk.
    const byStrike = new Map();
    const addSide = (o, key) => {
      const iv = parseFloat(o?.iv) || null;
      const moneyness = ((o?.strike / spot) - 1) * 100;
      // Same two gates as before: a zero/absent IV is not a reading, and the ±30%
      // band keeps deep wings from flattening the curve.
      if (!iv || !Number.isFinite(moneyness) || Math.abs(moneyness) >= 30) return;
      const row = byStrike.get(o.strike)
        || { strike: o.strike, moneyness, ivPut: null, ivCall: null };
      row[key] = iv;
      byStrike.set(o.strike, row);
    };
    (puts || []).forEach((p) => addSide(p, "ivPut"));
    (calls || []).forEach((c) => addSide(c, "ivCall"));

    // Merge Heston fitted IV if available
    return [...byStrike.values()]
      .sort((a, b) => a.moneyness - b.moneyness)
      .map((d) => {
        const hMatch = hestonSurface?.find((h) => h.strike === d.strike);
        return { ...d, hestonIV: hMatch ? parseFloat(hMatch.modelIV) : null };
      });
  }, [calls, puts, spot, hestonSurface]);

  if (!calls?.length && !puts?.length) return null;
  if (!(spot > 0)) return null;

  const header = (
    <div style={s.panelH}>
      <span style={s.panelT}>VOLATILITY SMILE</span>
      {/* ⚠ C.teal and C.amber are the SAME hex in this palette (#00FF87), so the
          wings take C.red / C.green — the house "data down / data up" tokens —
          and Heston keeps the accent. Three lines, three distinguishable colours. */}
      <span style={{ ...s.tag(), fontSize: 11 }}>
        {expiration ? <>Exp {expiration} · </> : null}
        IV vs Moneyness · <span style={{ color: C.red }}>Put wing</span>
        {" · "}<span style={{ color: C.green }}>Call wing</span>
        {hestonSurface?.length > 0 && <> · <span style={{ color: C.teal }}>Heston Fitted</span></>}
      </span>
    </div>
  );

  // ⚠ A non-empty chain can still yield nothing plottable — every strike stripped
  // by the ±30% moneyness filter, or no row carrying a usable IV. Say that
  // plainly instead of rendering an empty chart with full axes, which reads as a
  // flat market rather than as missing data.
  if (!withHeston.length) {
    return (
      <div style={s.panel}>
        {header}
        <div style={{ ...s.panelB, fontSize: 13, color: C.textDim }}>
          <AlertCircle size={10} style={{ marginRight: 6 }} />
          No strikes carrying implied vol within ±30% of {fmt(spot, 2)}
          {expiration ? ` for ${expiration}` : ""}.
        </div>
      </div>
    );
  }

  return (
    <div style={s.panel}>
      {header}
      <div style={s.panelB}>
        <div style={{ fontSize: 11, color: C.textDim, marginBottom: 6 }}>
          The smile is the market correcting for Black-Scholes constant-vol assumption.
          OTM puts typically carry higher IV (skew) due to hedging demand and jump risk pricing.
          Heston model fitted IV shows where the stochastic vol model expects IV to sit.
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <ComposedChart data={withHeston} margin={{ top: 4, right: 4, left: 0, bottom: 4 }}>
            {/* ⚠ type="number" is what makes moneyness a real scale. As a category
                axis every row was one equal-width slot, so the measured 0.00-1.30
                percentage-point strike gaps all rendered the same width and the
                curve's shape was not metrically true. */}
            <XAxis dataKey="moneyness" type="number" domain={["dataMin", "dataMax"]}
              tick={{ fontSize: 10, fill: C.textDim }} tickLine={false}
              tickFormatter={(v) => `${v > 0 ? "+" : ""}${fmt(v, 0)}%`} />
            <YAxis tick={{ fontSize: 10, fill: C.textDim }} tickLine={false} axisLine={false}
              tickFormatter={(v) => `${v}%`} domain={["auto", "auto"]} width={32} />
            {/* Tooltip filterNull defaults true, so a strike listing only one side
                shows only that side rather than a null row. */}
            {/* ⚠⚠ isAnimationActive on <Tooltip> is a DIFFERENT prop from the one
                on <Line> — setting it on the series does nothing for the box. Left
                at its recharts default the wrapper eases to each new position over
                400ms, so on a ladder whose ATM strikes sit ~3px apart the box is
                permanently chasing a target that moves faster than it animates. */}
            <Tooltip
              isAnimationActive={false}
              contentStyle={{ background: C.panel, border: `1px solid ${C.border}`, fontSize: 11, fontFamily: font }}
              labelStyle={{ color: C.text }}
              itemStyle={{ color: C.text }}
              formatter={(v, n) => [`${fmt(v, 1)}%`, n]}
              labelFormatter={(l) => `${parseFloat(l) > 0 ? "+" : ""}${parseFloat(l).toFixed(1)}% OTM`}
            />
            <Legend wrapperStyle={{ fontSize: 11, fontFamily: font, color: C.textDim }}
              iconSize={8} verticalAlign="bottom" height={18} />
            <ReferenceLine x={0} stroke={C.amber + "55"} strokeDasharray="3 2"
              label={{ value: "ATM", fill: C.amber, fontSize: 10 }} />
            {/* ⚠ isAnimationActive={false} on every series: the smile refetches on
                each toggle and ticker change, and the mount animation re-ran each
                time — hovering mid-animation reads interpolated geometry.
                ⚠ activeDot is explicit rather than left to Recharts' default,
                matching the price chart's precedent at the SMA/EMA lines.
                ⚠ connectNulls is deliberately NOT set: a strike with no listed put
                (or no call) is a real gap, and bridging it would draw a segment
                between two strikes that never traded that side. */}
            <Line type="monotone" dataKey="ivPut" stroke={C.red} strokeWidth={2}
              dot={{ r: 2.5, fill: C.red }} activeDot={{ r: 4, fill: C.red, stroke: C.bg, strokeWidth: 1 }}
              isAnimationActive={false} name="Put IV" />
            <Line type="monotone" dataKey="ivCall" stroke={C.green} strokeWidth={2}
              dot={{ r: 2.5, fill: C.green }} activeDot={{ r: 4, fill: C.green, stroke: C.bg, strokeWidth: 1 }}
              isAnimationActive={false} name="Call IV" />
            {hestonSurface?.length > 0 && (
              <Line type="monotone" dataKey="hestonIV" stroke={C.teal} strokeWidth={1.5}
                dot={{ r: 2, fill: C.teal }} activeDot={{ r: 3.5, fill: C.teal, stroke: C.bg, strokeWidth: 1 }}
                isAnimationActive={false} strokeDasharray="4 2" name="Heston IV" />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── SESSION RESTORE ──────────────────────────────────────────────────────────
// ⚠⚠ READ AT INITIALISATION, NOT IN AN EFFECT — AND THE DIFFERENCE WAS A BUG.
// This used to be a `useEffect(..., [])` that read these keys and called
// setSymbol/setTimeframe. It was declared AFTER the persist effect, and React runs
// mount effects in declaration order, so on every mount the sequence was:
//
//   1. persist effect fires with the useState INITIAL "SPY"
//      -> localStorage.setItem("fterm_chart_symbol", "SPY")   <- clobbers
//   2. restore effect reads the key it was just overwritten with
//      -> "SPY", every time
//
// Invisible while ChartsTab was the only writer, because the value it destroyed
// was the value it had just written. It became visible the moment the dark-pool
// and unusual-options tapes started SEEDING that key to deep-link a ticker: the
// seed was written, the tab switched, and the chart opened on SPY on every ticker.
// A lazy initialiser cannot lose that race — the value is read before the first
// render, so the persist effect's first run already carries the right symbol.
// ⚠ It also removes a wasted loadChart("SPY") that fired on EVERY entry to this
// tab, seeded or not: the old flow always rendered once with the initial state
// before the restore effect corrected it.
// ⚠ Named constants so the read and the write cannot drift apart. AltDataTab
// seeds CHART_SYMBOL_KEY by the same literal.
const CHART_SYMBOL_KEY = "fterm_chart_symbol";
// ⚠ Written by the CHART button on a dark-pool or unusual-options row so the
// chart opens with that layer already on. See pendingLayerRef.
const CHART_LAYER_KEY = "fterm_chart_layer";
const CHART_TF_KEY = "fterm_chart_tf";

// localStorage throws in private mode and in some embedded webviews; a missing
// preference is not an error state, so every failure resolves to the default.
function storedChart(key, fallback) {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

// ─── MAIN CHARTS TAB ─────────────────────────────────────────────────────────
export function ChartsTab({ keys, globalSignals }) {
  // ⚠ Lazy initialisers: the function form runs ONCE, before the first render,
  // so the stored symbol is in place before any effect can overwrite it.
  const [symbol,       setSymbol]       = useState(() => storedChart(CHART_SYMBOL_KEY, "SPY"));
  const [inputSymbol,  setInputSymbol]  = useState(() => storedChart(CHART_SYMBOL_KEY, "SPY"));
  const [searchQuery,  setSearchQuery]  = useState("");
  const [searchResults,setSearchResults]= useState([]);
  const [showSearch,   setShowSearch]   = useState(false);
  const [timeframe,    setTimeframe]    = useState(() => storedChart(CHART_TF_KEY, "1D"));
  const [bars,         setBars]         = useState([]);
  const [status,       setStatus]       = useState("offline");
  const [loading,      setLoading]      = useState(false);
  const [activeOverlays, setActiveOverlays] = useState(["SMA20", "VOLUME"]);
  const [activeIndicators, setActiveIndicators] = useState(["RSI", "MACD", "VOLUME"]);
  const [overlayData,  setOverlayData]  = useState({});
  const [rsiData,      setRsiData]      = useState([]);
  const [macdData,     setMacdData]     = useState({});
  const [currentPrice, setCurrentPrice] = useState(null);
  const [signals,      setSignals]      = useState([]);
  const [showPayoff,   setShowPayoff]   = useState(false);
  const [showSmile,    setShowSmile]    = useState(false);
  const [smileChain,   setSmileChain]   = useState(null);    // {calls, puts, expiration}
  const [smileStatus,  setSmileStatus]  = useState("idle");  // idle|loading|ready|empty|error
  // ── signal layers ──
  // ⚠ Data and visibility are separate. A layer with no data for this ticker is
  // not merely hidden — it has nothing to hide, and its pill is not rendered at
  // all. Visibility defaults ON whenever data arrives, per ticker.
  const [dpRaw,        setDpRaw]        = useState(null);
  const [uoRaw,        setUoRaw]        = useState(null);
  const [activeLayers, setActiveLayers] = useState([]);
  // ── drawing tools ──
  // drawNonce lets the SAME tool be re-armed after a drawing completes without
  // the effect short-circuiting on an unchanged tool id.
  const [drawTool,   setDrawTool]   = useState(null);
  const [drawNonce,  setDrawNonce]  = useState(0);
  const [clearNonce, setClearNonce] = useState(0);
  // ── fullscreen ──
  const [fullscreen, setFullscreen] = useState(false);
  const [viewH, setViewH] = useState(
    () => (typeof window === "undefined" ? 800 : window.innerHeight));
  const wsRef = useRef(null);
  // Supersedes in-flight signal-layer fetches when the ticker changes, the same
  // guard the smile fetch uses — a slow response for the previous symbol must
  // never land on the new one.
  const layerFetchRef = useRef(0);
  // ⚠⚠ READ ONCE, AT MOUNT, BEFORE ANY FETCH RESOLVES. A DP or UO row can ask
  // for its layer to arrive already on by writing "dp"/"uo" here. It is consumed
  // immediately — the key must not survive into the next navigation, or every
  // later visit would re-enable a layer the user had since turned off.
  // ⚠ The layer still only switches on if that layer HAS data; this seeds the
  // preference, it does not fabricate a toggle for an empty layer.
  const pendingLayerRef = useRef((() => {
    try {
      const v = localStorage.getItem(CHART_LAYER_KEY);
      if (v) localStorage.removeItem(CHART_LAYER_KEY);
      return v === "dp" || v === "uo" ? v : null;
    } catch { return null; }
  })());
  // Supersedes an in-flight smile fetch when the ticker changes mid-request —
  // same guard OptionsTab uses on its chain fetches, and for the same reason:
  // without it a slow response for the previous symbol can land after a fast one
  // for the current symbol and overwrite it.
  const smileFetchRef = useRef(0);

  // ── Fetch and compute everything ──
  const loadChart = useCallback(async (sym, tf) => {
    setLoading(true);
    setBars([]);
    setStatus("offline");

    const { bars: fetched, source } = await fetchBars(sym, tf, keys);
    if (!fetched.length) { setLoading(false); return; }

    setBars(fetched);
    setStatus(source === "alpaca" ? "live" : source === "yahoo" ? "delayed" : "offline");
    setCurrentPrice(fetched[fetched.length - 1]?.close);

    // Compute indicators
    const closes = fetched.map((b) => b.close);
    const newOverlay = {};

    if (activeOverlays.includes("SMA20"))  newOverlay.sma20  = calcSMA(closes, 20);
    if (activeOverlays.includes("SMA50"))  newOverlay.sma50  = calcSMA(closes, 50);
    if (activeOverlays.includes("SMA200")) newOverlay.sma200 = calcSMA(closes, 200);
    if (activeOverlays.includes("EMA20"))  newOverlay.ema20  = calcEMA(closes, 20);
    if (activeOverlays.includes("VWAP"))   newOverlay.vwap   = calcVWAP(fetched);
    if (activeOverlays.includes("BB")) {
      const bb = calcBollingerBands(closes);
      newOverlay.bbUpper = bb.upper;
      newOverlay.bbMid   = bb.sma;
      newOverlay.bbLower = bb.lower;
    }
    setOverlayData(newOverlay);

    if (activeIndicators.includes("RSI"))  setRsiData(calcRSI(closes));
    if (activeIndicators.includes("MACD")) setMacdData(calcMACD(closes));

    // Pull signal markers from globalSignals
    const sigMarkers = [];
    if (globalSignals?.squeezeScans) {
      const scanResult = globalSignals.squeezeScans.find((r) => r.ticker === sym);
      if (scanResult?.score >= 60) {
        sigMarkers.push({ date: fetched[fetched.length - 1]?.date, label: `SQZ ${scanResult.score}`, color: C.red });
      }
    }
    setSignals(sigMarkers);

    setLoading(false);
  }, [keys, activeOverlays, activeIndicators, globalSignals]);

  // ── Signal layers: fetch all three on symbol change ──
  // ⚠⚠ THREE INDEPENDENT FAILURES, NONE OF THEM FATAL. Promise.allSettled, not
  // Promise.all: one rejected fetch must not take the other two layers down with
  // it, and none of them may affect the price chart. Every fetcher already
  // resolves null on failure, so this is belt and braces — deliberately.
  // ⚠ NOT keyed on timeframe. Dark-pool prints and option strikes are both
  // price-domain facts; a 1D→1W switch does not change either of them, and
  // refetching on every timeframe click would triple the request rate for nothing.
  useEffect(() => {
    if (!symbol) return;
    const myId = ++layerFetchRef.current;
    setDpRaw(null); setUoRaw(null);
    setActiveLayers([]);
    setDrawTool(null);            // a new instrument starts with no armed tool
    (async () => {
      const [dpR, uoR] = await Promise.allSettled([
        fetchDarkPoolHistory(symbol),
        fetchUnusualForTicker(symbol),
      ]);
      if (myId !== layerFetchRef.current) return;   // superseded
      const dpv = dpR.status === "fulfilled" ? dpR.value : null;
      const uov = uoR.status === "fulfilled" ? uoR.value : null;
      setDpRaw(dpv); setUoRaw(uov);
      // ⚠ The requested layer is a PREFERENCE, applied only if that layer
      // actually resolved data. Forcing it on regardless would show an active
      // pill with nothing behind it.
      const want = pendingLayerRef.current;
      pendingLayerRef.current = null;
      const on = [
        ...(dpv ? ["dp"] : []),
        ...(uov ? ["uo"] : []),
      ];
      setActiveLayers(want && on.includes(want) ? [want, ...on.filter((x) => x !== want)] : on);
    })();
  }, [symbol]);

  // ── Fullscreen: Escape exits, and the viewport height drives the chart height ──
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === "Escape") setFullscreen(false); };
    const onResize = () => setViewH(window.innerHeight);
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    setViewH(window.innerHeight);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [fullscreen]);

  // ── Real-time WebSocket price update ──
  useEffect(() => {
    if (!keys.alpacaKey || !keys.alpacaSecret || !symbol) return;
    const ws = new WebSocket("wss://stream.data.alpaca.markets/v2/iex");
    wsRef.current = ws;
    ws.onopen = () => ws.send(JSON.stringify({ action: "auth", key: keys.alpacaKey, secret: keys.alpacaSecret }));
    ws.onmessage = (e) => {
      try {
        JSON.parse(e.data).forEach((m) => {
          if (m.T === "success" && m.msg === "authenticated") {
            ws.send(JSON.stringify({ action: "subscribe", trades: [symbol] }));
          }
          if (m.T === "t" && m.S === symbol) {
            setCurrentPrice(m.p);
            // Update last bar close in real-time
            setBars((prev) => {
              if (!prev.length) return prev;
              const updated = [...prev];
              const last = { ...updated[updated.length - 1] };
              last.close = m.p;
              last.high  = Math.max(last.high, m.p);
              last.low   = Math.min(last.low, m.p);
              updated[updated.length - 1] = last;
              return updated;
            });
          }
        });
      } catch {}
    };
    ws.onclose = () => {};
    return () => ws.close();
  }, [symbol, keys]);

  // ── Ticker search ──
  useEffect(() => {
    if (!searchQuery) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      const results = await searchTickers(searchQuery, keys);
      setSearchResults(results);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, keys]);

  // ── Load on symbol/timeframe change ──
  useEffect(() => {
    loadChart(symbol, timeframe);
    // ⚠ Wrapped: a storage failure must not take the chart down with it. The
    // load above has already happened by this point.
    try {
      localStorage.setItem(CHART_SYMBOL_KEY, symbol);
      localStorage.setItem(CHART_TF_KEY, timeframe);
    } catch { /* private mode — the session just will not persist */ }
  }, [symbol, timeframe]);

  // ── Vol smile: load this chart's OWN chain ──
  // Keyed on [showSmile, symbol] — it fires the moment the tool is switched on and
  // again on every ticker change while it stays on. Deliberately NOT keyed on
  // timeframe: the smile is a snapshot of one expiry, not a function of the bar
  // interval, so a 1D→1W switch must not refetch it.
  // ⚠ smileChain is cleared up front on every run. Without that, changing ticker
  // with the smile open would keep the PREVIOUS symbol's chain on screen against
  // the new symbol's spot until the new fetch landed — reintroducing, for a few
  // hundred milliseconds, the exact mismatch this change exists to remove.
  useEffect(() => {
    setSmileChain(null);
    if (!showSmile || !symbol) { setSmileStatus("idle"); return; }
    const myId = ++smileFetchRef.current;
    setSmileStatus("loading");
    (async () => {
      try {
        const res = await fetchSmileChain(symbol);
        if (myId !== smileFetchRef.current) return;   // superseded by a newer ticker
        setSmileChain({ ...res, symbol });
        setSmileStatus(res.calls.length || res.puts.length ? "ready" : "empty");
      } catch {
        if (myId !== smileFetchRef.current) return;
        setSmileStatus("error");
      }
    })();
  }, [showSmile, symbol]);

  // Toggle overlay/indicator
  const toggleOverlay = (name) => {
    setActiveOverlays((p) => p.includes(name) ? p.filter((x) => x !== name) : [...p, name]);
  };
  const toggleIndicator = (name) => {
    setActiveIndicators((p) => p.includes(name) ? p.filter((x) => x !== name) : [...p, name]);
  };

  // ── Smile data source ──
  // ⚠ The bus chain is usable ONLY when it is for this chart's symbol. It carries
  // `symbol` now that the OptionsTab push is stamped; an older bundle without the
  // stamp yields undefined, which never matches, so it is ignored rather than
  // rendered against the wrong spot — the failure mode is a blank panel for one
  // fetch, never a wrong curve. Charts fetches its own chain regardless; the bus
  // copy only avoids a blank first paint when OPTIONS is already on this ticker.
  const busChain = globalSignals?.optionsChain;
  const seedChain = busChain?.symbol === symbol ? busChain : null;
  const activeChain = smileChain || seedChain;
  const smilePoints = (activeChain?.calls?.length || 0) + (activeChain?.puts?.length || 0);

  // ⚠⚠ The Heston overlay is gated on symbol AND expiry. hestonFitChain builds its
  // surface from ONE expiry — the FRONT one when the fit is cross-sectional — while
  // this smile shows the nearest expiry Charts fetched. Usually the same tenor, but
  // not always, and the match at :854 is on strike alone: an underlying's strike
  // ladder is typically identical across expiries, so a mismatched surface would
  // line up perfectly and draw one maturity's model IVs across another's smile with
  // no visible tell. Both sides are sliced to YYYY-MM-DD before comparing.
  // ⚠ Fails CLOSED. A null expiry (OptionsTab could not determine which expiry the
  // fit used) or an unstamped bare-array payload from an older bundle both drop to
  // [] — the smile still renders, just without the model line. Omitting a
  // comparison is recoverable; drawing a wrong one is not.
  const busHeston = globalSignals?.hestonSurface;
  const hestonExp = busHeston?.expiration ? String(busHeston.expiration).slice(0, 10) : null;
  const smileExp  = activeChain?.expiration ? String(activeChain.expiration).slice(0, 10) : null;
  // ⚠ Memoised for the same reason as the smile rows: this expression built a
  // FRESH [] literal on every render whenever the gate failed — which is the
  // common case — so VolSmile's data memo would have been invalidated once per
  // second by App's clock tick no matter what it depended on.
  const hestonSurface = useMemo(
    () => (busHeston?.symbol === symbol && hestonExp && smileExp && hestonExp === smileExp
      ? (busHeston.surface || [])
      : []),
    [busHeston, symbol, hestonExp, smileExp]
  );

  // ── Derived signal-layer models ──
  // ⚠ Memoised on the RAW payload and the spot, not on the toggle. Binning a
  // week of prints is not free, and recomputing it every time the user flicks a
  // pill — or every time App's one-second clock re-renders this tab — would be
  // pure waste. Toggling visibility must not touch the maths.
  const dpModel = useMemo(
    () => binDarkPoolPrints(dpRaw, symbol, currentPrice),
    [dpRaw, symbol, currentPrice]);
  const uoModel = useMemo(() => groupUnusualByStrike(uoRaw), [uoRaw]);
  const layerShow = useMemo(() => ({
    dp: activeLayers.includes("dp"),
    uo: activeLayers.includes("uo"),
  }), [activeLayers]);
  const availableLayers = SIGNAL_LAYERS.filter((l) =>
    (l.id === "dp" && dpModel) ||
    (l.id === "uo" && uoModel));
  const chartHeight = fullscreen ? Math.max(420, viewH - 330) : 320;

  // Price change from first bar
  const firstClose = bars[0]?.close;
  const priceChange = currentPrice && firstClose ? currentPrice - firstClose : null;
  const pricePct    = priceChange && firstClose ? (priceChange / firstClose) * 100 : null;

  return (
    <div style={fullscreen ? {
      position: "fixed", inset: 0, zIndex: 9999, background: C.bg,
      overflowY: "auto", padding: 10,
    } : undefined}>
      {/* ── Toolbar ── */}
      <div style={{
        ...s.panel,
        padding: "8px 10px",
        marginBottom: 10,
        display: "flex",
        gap: 8,
        alignItems: "center",
        flexWrap: "wrap",
      }}>
        {/* Ticker search */}
        <div style={{ position: "relative" }}>
          <div style={{ display: "flex", gap: 6 }}>
            <input
              style={{ ...s.inp, width: 90, textTransform: "uppercase" }}
              value={searchQuery || inputSymbol}
              placeholder="TICKER"
              onChange={(e) => {
                setInputSymbol(e.target.value.toUpperCase());
                setSearchQuery(e.target.value.toUpperCase());
                setShowSearch(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  const sym = inputSymbol.trim().toUpperCase();
                  if (sym) { setSymbol(sym); setShowSearch(false); setSearchQuery(""); }
                }
              }}
              onFocus={() => setShowSearch(true)}
            />
            <button style={s.btn("amber")} onClick={() => {
              const sym = inputSymbol.trim().toUpperCase();
              if (sym) { setSymbol(sym); setShowSearch(false); setSearchQuery(""); }
            }}>
              LOAD
            </button>
          </div>
          {/* Dropdown results */}
          {showSearch && searchResults.length > 0 && (
            <div style={{
              position: "absolute",
              top: "100%",
              left: 0,
              zIndex: 50,
              background: C.panel,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              minWidth: 240,
              marginTop: 2,
              boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
            }}>
              {searchResults.map((r) => (
                <div
                  key={r.symbol}
                  style={{ padding: "7px 10px", cursor: "pointer", borderBottom: `1px solid ${C.border}` }}
                  onMouseDown={() => {
                    setSymbol(r.symbol);
                    setInputSymbol(r.symbol);
                    setSearchQuery("");
                    setShowSearch(false);
                  }}
                >
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.amber }}>{r.symbol}</div>
                  <div style={{ fontSize: 11, color: C.textDim }}>{r.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Current price display */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {currentPrice && (
            <span style={{ fontSize: 16, fontWeight: 700, color: C.text, fontFamily: font }}>
              ${fmt(currentPrice)}
            </span>
          )}
          {pricePct != null && (
            <span style={s.badge(pricePct >= 0)}>{pct(pricePct)}</span>
          )}
        </div>

        <div style={{ flex: 1 }} />

        {/* Timeframe selector */}
        <div style={{ display: "flex", gap: 3 }}>
          {TIMEFRAMES.map((tf) => (
            <button
              key={tf.label}
              style={{ ...s.btn(timeframe === tf.label ? "amber" : "default"), padding: "3px 7px", fontSize: 11 }}
              onClick={() => setTimeframe(tf.label)}
            >
              {tf.label}
            </button>
          ))}
        </div>

        <button style={{ ...s.btn(), padding: "3px 7px" }} onClick={() => loadChart(symbol, timeframe)}>
          <RefreshCw size={10} />
        </button>
        <DataStatus status={status} source={status === "live" ? "" : ""} />
      </div>

      {/* ── Overlay toggles ── */}
      <div style={{ display: "flex", gap: 5, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span style={{ fontSize: 11, color: C.textDim }}>OVERLAYS:</span>
        {OVERLAYS.map((name) => (
          <button
            key={name}
            style={{
              ...s.btn(activeOverlays.includes(name) ? "teal" : "default"),
              padding: "2px 8px",
              fontSize: 11,
            }}
            onClick={() => { toggleOverlay(name); setTimeout(() => loadChart(symbol, timeframe), 0); }}
          >
            {name}
          </button>
        ))}
        <span style={{ fontSize: 11, color: C.textDim, marginLeft: 6 }}>PANES:</span>
        {INDICATORS.map((name) => (
          <button
            key={name}
            style={{
              ...s.btn(activeIndicators.includes(name) ? "amber" : "default"),
              padding: "2px 8px",
              fontSize: 11,
            }}
            onClick={() => { toggleIndicator(name); setTimeout(() => loadChart(symbol, timeframe), 0); }}
          >
            {name}
          </button>
        ))}
        <span style={{ fontSize: 11, color: C.textDim, marginLeft: 6 }}>TOOLS:</span>
        <button style={{ ...s.btn(showPayoff ? "amber" : "default"), padding: "2px 8px", fontSize: 11 }}
          onClick={() => setShowPayoff((p) => !p)}>PAYOFF</button>
        <button style={{ ...s.btn(showSmile ? "amber" : "default"), padding: "2px 8px", fontSize: 11 }}
          onClick={() => setShowSmile((p) => !p)}>VOL SMILE</button>
        {/* ⚠ Clicking the armed tool again DISARMS it — otherwise the only way
            back to plain crosshair/pan is a symbol change. */}
        {DRAW_TOOLS.map((t) => (
          <button
            key={t.id}
            title={`Draw: ${t.label.toLowerCase()}`}
            style={{ ...s.btn(drawTool === t.id ? "purple" : "default"),
              padding: "2px 8px", fontSize: 11 }}
            onClick={() => {
              setDrawTool((p) => (p === t.id ? null : t.id));
              setDrawNonce((n) => n + 1);
            }}
          >
            {t.label}
          </button>
        ))}
        <button
          title="Clear all drawings"
          style={{ ...s.btn(), padding: "2px 6px", fontSize: 11 }}
          onClick={() => { setClearNonce((n) => n + 1); setDrawTool(null); }}
        >
          <Trash2 size={10} />
        </button>
        {/* ⚠ Layer pills appear ONLY for layers that actually have data on this
            ticker. A permanently dead pill on every equity would read as a broken
            feature rather than an absent one. */}
        {availableLayers.length > 0 && (
          <span style={{ fontSize: 11, color: C.textDim, marginLeft: 6 }}>SIGNALS:</span>
        )}
        {availableLayers.map((l) => (
          <button
            key={l.id}
            style={{ ...s.btn(activeLayers.includes(l.id) ? "blue" : "default"),
              padding: "2px 8px", fontSize: 11 }}
            onClick={() => setActiveLayers((p) =>
              p.includes(l.id) ? p.filter((x) => x !== l.id) : [...p, l.id])}
          >
            {l.label}
          </button>
        ))}
      </div>

      {/* ── Overlay legend ── */}
      {activeOverlays.length > 0 && (
        <div style={{ display: "flex", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
          {[
            { name: "SMA20",  color: "#f59e0b", label: "SMA 20" },
            { name: "SMA50",  color: "#3b82f6", label: "SMA 50" },
            { name: "SMA200", color: "#a855f7", label: "SMA 200"},
            { name: "EMA20",  color: "#00ff87", label: "EMA 20" },
            { name: "VWAP",   color: "#ec4899", label: "VWAP"   },
            { name: "BB",     color: "#9D4EDD", label: "Bollinger (filled)" },
          ].filter((o) => activeOverlays.includes(o.name)).map((o) => (
            <div key={o.name} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 20, height: 2, background: o.color, borderRadius: 1 }} />
              <span style={{ fontSize: 11, color: C.textDim }}>{o.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Main chart panel ── */}
      <div style={s.panel}>
        <div style={s.panelH}>
          <span style={s.panelT}>{symbol} — {timeframe} — {bars.length} BARS</span>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {bars.length > 0 && (
              <span style={{ fontSize: 11, color: C.textDim }}>
                {bars[0]?.date} → {bars[bars.length-1]?.date}
              </span>
            )}
            <button
              title={fullscreen ? "Exit fullscreen (Esc)" : "Fullscreen"}
              style={{ ...s.btn(fullscreen ? "amber" : "default"), padding: "2px 6px" }}
              onClick={() => setFullscreen((p) => !p)}
            >
              {fullscreen ? <Minimize2 size={11} /> : <Maximize2 size={11} />}
            </button>
          </div>
        </div>
        <div style={s.panelB}>
          {loading ? (
            <div style={{ height: chartHeight, display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim, fontSize: 14 }}>
              LOADING {symbol} {timeframe} DATA...
            </div>
          ) : bars.length > 0 ? (
            <>
              {/* ⚠ RSI and MACD are no longer sibling charts — they are panes of
                  this one, so they are props, not components. */}
              <LWPriceChart
                bars={bars}
                overlayData={overlayData}
                signals={signals}
                showVolume={activeIndicators.includes("VOLUME")}
                showRSI={activeIndicators.includes("RSI")}
                showMACD={activeIndicators.includes("MACD")}
                rsiData={rsiData}
                macdData={macdData}
                drawTool={drawTool}
                drawNonce={drawNonce}
                clearNonce={clearNonce}
                onDrawComplete={() => setDrawTool(null)}
                height={chartHeight}
                dp={dpModel}
                uo={uoModel}
                dpRows={dpRaw}
                uoRows={uoRaw}
                layerShow={layerShow}
              />
            </>
          ) : (
            <div style={{ height: chartHeight, display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim, fontSize: 14 }}>
              <AlertCircle size={12} style={{ marginRight: 6 }} />
              No data — check API keys or try a different symbol
            </div>
          )}
        </div>
      </div>

      {/* ── AI Analysis ── */}
      {(keys.anthropicKey || window.QUANT_SIGNAL_BACKEND) && bars.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <AnalystButton
            anthropicKey={keys.anthropicKey}
            panel="charts"
            label="SYNTHESIZE"
            context={
              `Price action analysis for ${symbol} (${timeframe} chart, ${bars.length} bars):\n\n` +
              `Current price: $${fmt(currentPrice)}\n` +
              `Period range: $${fmt(Math.min(...bars.map((b) => b.low)))} — $${fmt(Math.max(...bars.map((b) => b.high)))}\n` +
              `Period open: $${fmt(bars[0]?.open)}, Period close: $${fmt(bars[bars.length-1]?.close)}\n` +
              `Period change: ${pct(pricePct)}\n` +
              `Average volume: ${fmtK(Math.round(bars.reduce((a, b) => a + b.volume, 0) / bars.length))}\n` +
              (activeOverlays.includes("SMA20") && overlayData.sma20 ? `SMA 20: $${fmt(overlayData.sma20[overlayData.sma20.length-1])}\n` : "") +
              (activeOverlays.includes("SMA50") && overlayData.sma50 ? `SMA 50: $${fmt(overlayData.sma50[overlayData.sma50.length-1])}\n` : "") +
              (rsiData.length > 0 ? `RSI (14): ${fmt(rsiData[rsiData.length-1], 1)}\n` : "") +
              (macdData.macdLine?.length > 0 ? `MACD: ${fmt(macdData.macdLine[macdData.macdLine.length-1], 3)}, Signal: ${fmt(macdData.signalLine?.[macdData.signalLine.length-1], 3)}\n` : "") +
              `\nAnalyze: the price structure and trend on this timeframe, ` +
              `what the volume pattern confirms or contradicts, ` +
              `key technical levels visible in the data, ` +
              `what the RSI and MACD configuration implies about momentum, ` +
              `and any notable structural observations. ` +
              `Be specific about price levels and percentages.` +
              (() => {
                // Cross-asset context — only what actually exists in globalSignals.
                const marketContext = [];
                if (globalSignals?.vix?.current) {
                  marketContext.push(`VIX: ${globalSignals.vix.current}`);
                }
                if (globalSignals?.activeSignals?.length > 0) {
                  const signalNames = globalSignals.activeSignals
                    .map((sig) => sig.title)
                    .slice(0, 3)
                    .join(", ");
                  marketContext.push(`ACTIVE SIGNALS: ${signalNames}`);
                }
                if (globalSignals?.portfolioExposures?.beta) {
                  marketContext.push(`MARKET BETA: ${globalSignals.portfolioExposures.beta}`);
                }
                return marketContext.length > 0
                  ? `\n\nMARKET CONTEXT:\n` + marketContext.join(" | ")
                  : "";
              })()
            }
          />
        </div>
      )}

      {/* ── Ticker news ── */}
      <TickerNewsPanel ticker={symbol} keys={keys} />

      {/* ── Options tools ── */}
      {showPayoff && (
        <PayoffDiagram spot={currentPrice} symbol={symbol} />
      )}

      {showSmile && smilePoints > 0 && (
        <VolSmile
          calls={activeChain.calls || []}
          puts={activeChain.puts || []}
          spot={currentPrice}
          hestonSurface={hestonSurface}
          expiration={activeChain.expiration}
        />
      )}

      {/* ⚠ The old copy here told the user to go load a chain in the OPTIONS tab.
          That was true of the old design and is now wrong — Charts loads its own,
          so the only honest states left are in-flight, genuinely nothing to show,
          and upstream failure. */}
      {showSmile && smilePoints === 0 && (
        <div style={{ ...s.panel, padding: "10px 12px", color: C.textDim, fontSize: 13 }}>
          <AlertCircle size={10} style={{ marginRight: 6 }} />
          {smileStatus === "error"
            ? `Could not load the options chain for ${symbol} — try again in a moment.`
            : smileStatus === "empty"
            ? `No listed options with implied vol for ${symbol}.`
            : `Loading options chain for ${symbol}…`}
        </div>
      )}

      <div style={{ fontSize: 11, color: C.textDim, marginTop: 8 }}>
        {status === "live" ? "Real-time" : "Delayed"} OHLCV{status === "live" ? "" : ""} ·
        Indicators: SMA, EMA, VWAP, Bollinger Bands, RSI (14), MACD (12,26,9) ·
        Drawing tools: trend, horizontal, rectangle, Fibonacci, text ·{" "}
        <span style={{ color: "#FFD700", fontWeight: 700 }}>Not a recommendation.</span>
      </div>
    </div>
  );
}
